import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangleIcon,
  BookOpenIcon,
  CheckIcon,
  ClipboardListIcon,
  EyeOffIcon,
  FilePlusIcon,
  FileTextIcon,
  ListChecksIcon,
  Loader2Icon,
  MapPinIcon,
  MicIcon,
  PlayIcon,
  RotateCcwIcon,
  SquareIcon,
  Trash2Icon,
  UploadCloudIcon,
} from 'lucide-react';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ApiError, apiGet, apiPut } from '../api/client.js';
import { CopyFieldButton } from '../components/CopyFieldButton.js';
import { maakVerwijsbriefUitSara } from '../lib/verwijsbrief.js';
import type { SaraConversationMetrics, SaraSoepStyle } from '@kennisbank/shared';
import {
  baseMimeType,
  bufferAppend,
  bufferClear,
  bufferInit,
  bufferLoad,
  classifyRecording,
  deleteSaraTask,
  fetchSaraTask,
  fetchSaraTasks,
  finalizeRecording,
  isMobileDevice,
  MIN_RATE_CHECK_SEC,
  pickRecordMime,
  regenerateSaraTask,
  requestUploadUrl,
  resumableUpload,
  warmupTranscriber,
  type BufferedRecording,
  type SaraTaskListItem,
  type SaraTaskStatus,
  type SaraTaskView,
} from '../lib/sara.js';

// Fase 4.2 (ADR 0009): het browser-opnamepad. De opname draait lokaal met
// MediaRecorder (Opus/WebM, of AAC/MP4 in Safari); chunks gaan tijdens de
// opname naar IndexedDB zodat een crash geen consult kost; de audio wordt
// hervatbaar geüpload naar een GCS signed URL; daarna verwerkt de centrale
// pijplijn (transcriberen → pseudonimiseren → samenvatten) server-side en
// pollt deze pagina op status. De ruwe audio en het ruwe transcript verlaten
// de browser alleen richting de eigen pijplijn; de POH controleert het
// gepseudonimiseerde SOEP-concept en bevestigt (hard rule 3: mens keurt goed
// vóór het HIS). Achter de server-flag SARA_PIPELINE_ENABLED: staat die uit,
// dan geeft de eerste call 404 en tonen we een nette melding in plaats van te
// crashen.
//
// Het opnameformaat is niet langer een constante: `pickRecordMime()` kiest het
// per browser en `baseMimeType()` maakt daar het basistype van. Dat basistype
// gaat naar de crash-buffer, de blob, de upload-URL en de resumable sessie, en
// reist zo mee met de opname zelf. Eerder stond hier één hardgecodeerde
// `UPLOAD_MIME = 'audio/webm'`; een MP4-opname zou daarmee als WebM
// ondertekend, opgeslagen en aan de transcriber aangeboden worden.

type Phase =
  | 'idle'
  | 'recording'
  | 'uploading'
  | 'processing'
  | 'review'
  | 'saved'
  | 'failed'
  | 'recovery'
  | 'unavailable'
  // Test-/demo-opnamepad: een gesimuleerde opname die géén microfoon, upload,
  // GPU-pijplijn of netwerkcall gebruikt. Klik opnemen → stop → de drie
  // pijplijnstappen spelen af (getimed) → een review met bewerkbare
  // samenvatting + transcript → client-side omzetten naar een voorbeeld-SOEP.
  // Bedoeld om de hele UX betrouwbaar te demonstreren zonder de doorlooptijd
  // en afhankelijkheden van de echte pijplijn.
  | 'test-recording'
  | 'test-processing'
  | 'test-review';

/** Wat er met de audio gebeurd is bij een mislukking; zie `failedAudio`. */
type FailedAudio = 'none' | 'retryable' | 'thin' | 'discarded';

type Soep = { S: string; O: string; E: string; P: string };

const EMPTY_SOEP: Soep = { S: '', O: '', E: '', P: '' };

// Voorbeeldconsulten voor de test workflow: vier realistische, volledig
// SYNTHETISCHE POH-GGZ-consulten, al in gepseudonimiseerde vorm (zoals de
// review ze uit de echte pijplijn krijgt). De SOEP's en samenvattingen zijn
// ECHTE uitvoer van de productieprompt (gemini-2.5-flash, gegenereerd
// 20-8-2026), per schrijfstijl, met dezelfde bullet-normalisatie als de
// pijplijn — zodat de demo de pijplijn accuraat weerspiegelt en de
// stijlkeuze volgt. De samenvatting is stijl-onafhankelijk. Elke demo-opname
// toont het volgende consult. Medicatie ligt bij de huisarts, niet de
// POH-GGZ (correctie Jos 20-8): de POH koppelt alleen terug.
const DEMO_CONSULTEN: {
  transcript: string;
  summary: string;
  soep: Record<SaraSoepStyle, Soep>;
}[] = [
  // vervolgconsult spanningsklachten
  {
    transcript: `POH: Goedemorgen mevrouw [NAAM_1], kom binnen. Hoe is het de afgelopen twee weken gegaan?

Cliënt: Eerlijk gezegd wisselend. De eerste week ging best goed, ik heb drie keer die wandeling gemaakt die we hadden afgesproken. Maar vorige week donderdag kreeg ik een mail van mijn leidinggevende over de herstructurering en toen was ik het eigenlijk meteen weer kwijt.

POH: Wat merkte u toen?

Cliënt: Dat bekende gevoel, druk op de borst, malende gedachten. Ik lag 's nachts weer wakker van drie tot half zes. En dan de volgende dag... dan lukt er gewoon niks. Dan zit ik op de bank en het enige wat ik doe is piekeren over of ik mijn baan nog wel houd.

POH: U zegt: dan lukt er niks. Hoe zagen die dagen eruit?

Cliënt: Ik heb me vrijdag ziek gemeld. Dat voelde als falen, want het ging net wat beter. Het weekend was ik vooral moe. Zaterdag heb ik wel met mijn dochter naar de markt gewild, maar ik ben halverwege omgedraaid omdat het te druk was in mijn hoofd.

POH: En het slapen, u noemde dat net al. Is dat elke nacht zo, of wisselt dat?

Cliënt: Sinds die mail eigenlijk elke nacht. Inslapen gaat wel, maar ik word rond drie uur wakker en dan begint het malen. Over werk, over geld, over wat er gebeurt als ik afgekeurd word. Terwijl dat helemaal niet aan de orde is, dat weet ik ook wel.

POH: U herkent dus zelf al dat die gedachten verder gaan dan de feiten. Dat is precies waar we het vorige keer over hadden. Lukte het om de piekerkwartier-oefening te doen?

Cliënt: De eerste week wel, en dat hielp echt. Vorige week ben ik het vergeten, of nou ja, vergeten... het voelde zinloos.

POH: Dat is heel herkenbaar bij een terugval na een stressmoment. Belangrijk is dat de eerste week liet zien dat het wél werkt. Hoe staat het met de alcohol, u zou daar op letten?

Cliënt: Beter. Doordeweeks niks meer, alleen vrijdag twee glazen wijn. Dat was eerst elke avond, dus dat is echt anders.

POH: Dat is een flinke stap, goed om te benoemen. Eten gaat dat gewoon door?

Cliënt: Redelijk. Minder trek dan normaal, maar ik eet wel drie keer per dag. Mijn man kookt veel, dat scheelt.

POH: Zijn er momenten geweest dat u dacht: het hoeft van mij allemaal niet meer?

Cliënt: Nee, dat niet. Het is zwaar, maar zo somber ben ik niet. Ik wil er juist vanaf, ik wil gewoon weer normaal kunnen werken en er zijn voor de kinderen.

POH: Goed dat u dat zo helder zegt. Als ik het samenvat: de eerste week ging het beter met wandelen en het piekerkwartier, toen kwam die mail over de herstructurering en zakte het terug, met vooral slecht doorslapen en piekeren over werk. De ziekmelding voelde als falen. Het alcoholgebruik is duidelijk minder. Klopt dat?

Cliënt: Ja, dat klopt precies.

POH: Dan stel ik voor dat we het piekerkwartier weer oppakken, en ik wil samen met u kijken naar een gesprek met uw leidinggevende of de bedrijfsarts over die herstructurering, zodat de onzekerheid concreter wordt. Onzekerheid voedt het piekeren. Zullen we over twee weken een vervolgafspraak plannen en dan ook de vragenlijst opnieuw invullen om het beloop te volgen?

Cliënt: Dat is goed. En dat gesprek met de bedrijfsarts, kunt u daarbij helpen met wat ik dan moet zeggen?

POH: Zeker, daar maken we volgende keer samen een voorbereiding voor. U mag ook eerder bellen als het doorslapen erger wordt of het piekeren de overhand krijgt.`,
    summary: `Cliënt [NAAM_1] rapporteert een wisselend beloop sinds het laatste consult. De eerste week ging het beter met wandelen en het piekerkwartier. Vorige week donderdag leidde een mail over herstructurering op het werk tot een terugval. Klachten omvatten druk op de borst, malende gedachten, en slecht doorslapen (wakker worden rond 3 uur en piekeren over werk, geld, afkeuring). Dit resulteerde in een ziekmelding en moeheid, waarbij sociale activiteiten (marktbezoek met dochter) werden afgebroken door overprikkeling. Het piekerkwartier werd vergeten/voelde zinloos. Positief is dat het alcoholgebruik is verminderd (doordeweeks geen alcohol, vrijdag 2 glazen wijn). Eetlust is verminderd, maar eetpatroon wordt gehandhaafd. Er zijn geen suïcidale gedachten. De hulpvraag richt zich op het verminderen van de klachten om weer normaal te kunnen werken en er te zijn voor de kinderen. Plan is om het piekerkwartier weer op te pakken en samen met de POH een gesprek met de leidinggevende of bedrijfsarts voor te bereiden om de onzekerheid rond de herstructurering te verminderen. Over twee weken volgt een vervolgafspraak met herhaling van de vragenlijst. Cliënt mag eerder contact opnemen bij verergering van klachten.`,
    soep: {
      bondig: {
        S: `Cliënt rapporteert een wisselend beloop: de eerste week ging het goed met wandelen en het piekerkwartier, maar na een mail over herstructurering op werk trad een terugval op. Klachten omvatten druk op de borst, malende gedachten, 's nachts wakker liggen van 3 tot 5:30 uur en piekeren over baanbehoud, werk, geld en afkeuring. Dit leidde tot een ziekmelding en moeheid; een gepland marktbezoek met dochter werd afgebroken door overprikkeling. Het piekerkwartier werd vorige week vergeten/voelde zinloos. Alcoholgebruik is verminderd (doordeweeks geen, vrijdag 2 glazen wijn). Eetlust is minder, maar eet 3x per dag. Cliënt heeft geen suïcidale gedachten en wil juist van de klachten af om weer normaal te kunnen werken en er te zijn voor de kinderen.`,
        O: `Cliënt bevestigt de samenvatting van de POH.`,
        E: `Terugval in piekerklachten en slaapproblemen, gepaard gaand met somatische spanningsklachten (druk op de borst), na een stressor (mail over herstructurering). Eerdere interventies waren effectief, wat duidt op responsiviteit. Geen suïcidale ideatie.`,
        P: `Piekerkwartier weer oppakken. Samen met POH voorbereiden op gesprek met leidinggevende of bedrijfsarts over herstructurering. Vervolgafspraak over twee weken, inclusief opnieuw invullen vragenlijst. Cliënt mag eerder bellen bij verergering van doorslaapproblemen of piekeren.`,
      },
      steekwoorden: {
        S: `- Wisselend beloop afgelopen 2 weken.
- Eerste week ging goed: 3x wandelen, piekerkwartier hielp.
- Vorige week donderdag mail leidinggevende over herstructurering -> terugval.
- Klachten bij terugval: druk op borst, malende gedachten, 's nachts wakker van 3:00 tot 5:30.
- Volgende dag lukt niks, zit op de bank, piekeren over baanbehoud.
- Vrijdag ziek gemeld, voelde als falen.
- Weekend moe.
- Zaterdag met dochter naar markt gewild, halverwege omgedraaid wegens te druk in hoofd.
- Sinds mail elke nacht slecht doorslapen: inslapen gaat wel, wakker rond 3:00, malen over werk, geld, afkeuring (weet dat dit niet aan de orde is).
- Piekerkwartier vorige week vergeten/voelde zinloos.
- Alcoholgebruik verbeterd: doordeweeks niks, vrijdag 2 glazen wijn (voorheen elke avond).
- Eten redelijk: minder trek, wel 3x per dag, man kookt veel.
- Geen gedachten van 'het hoeft allemaal niet meer', wil er juist vanaf, weer normaal werken en er zijn voor de kinderen.
- Bevestigt POH-samenvatting van beloop.`,
        O: `- Cliënt herkent zelf dat gedachten verder gaan dan feiten.`,
        E: `- Terugval in klachten (piekeren, slaapproblemen, vermoeidheid) na stressor (mail herstructurering).
- Eerdere interventies (wandelen, piekerkwartier) waren effectief, duidt op responsiviteit.
- Geen suïcidale ideatie.`,
        P: `- Piekerkwartier weer oppakken.
- Samen met POH kijken naar gesprek met leidinggevende/bedrijfsarts over herstructurering.
- Vervolgafspraak over 2 weken plannen.
- Vragenlijst opnieuw invullen om beloop te volgen.
- POH helpt met voorbereiding gesprek bedrijfsarts volgende keer.
- Cliënt mag eerder bellen bij verergering doorslaapproblemen of overhand krijgen piekeren.`,
      },
      uitgebreid: {
        S: `Cliënt rapporteert een wisselend beloop de afgelopen twee weken. De eerste week ging goed, met drie keer wandelen en het piekerkwartier hielp. Vorige week donderdag ontving cliënt een mail van de leidinggevende over herstructurering, waarna een terugval optrad. Klachten omvatten druk op de borst, malende gedachten en 's nachts wakker liggen van 3:00 tot 5:30. Overdag lukt dan niks, cliënt zit op de bank en piekert over baanbehoud. Vrijdag heeft cliënt zich ziek gemeld, wat als falen voelde. Het weekend was cliënt vooral moe; een poging om met dochter naar de markt te gaan, werd halverwege afgebroken vanwege drukte in het hoofd. Sinds de mail is het doorslapen elke nacht slecht; inslapen gaat wel, maar rond 3:00 begint het malen over werk, geld en afkeuring, hoewel cliënt zelf erkent dat dit niet aan de orde is. Het piekerkwartier is vorige week vergeten of voelde zinloos. Het alcoholgebruik is verbeterd: doordeweeks niks, alleen vrijdag twee glazen wijn, wat voorheen elke avond was. Eten gaat redelijk, met minder trek maar wel drie maaltijden per dag, mede dankzij de man die veel kookt. Cliënt heeft geen suïcidale gedachten en wil juist weer normaal kunnen werken en er zijn voor de kinderen. Cliënt bevestigt de samenvatting van de POH en vraagt hulp bij de voorbereiding van een gesprek met de bedrijfsarts.`,
        O: `Geen aanvullende objectieve bevindingen.`,
        E: `Er is sprake van een terugval in klachten van piekeren, slaapproblemen en verminderde activiteit, uitgelokt door een stressor (mail over herstructurering). Eerdere interventies (wandelen, piekerkwartier) waren effectief, wat duidt op een goede prognose bij herstart van deze interventies en aanpak van de stressor. Er zijn geen aanwijzingen voor suïcidaliteit.`,
        P: `Het piekerkwartier wordt weer opgepakt. Er wordt samen met de POH gekeken naar een gesprek met de leidinggevende of bedrijfsarts over de herstructurering om de onzekerheid te concretiseren. Een vervolgafspraak is gepland over twee weken, waarbij ook de vragenlijst opnieuw wordt ingevuld om het beloop te volgen. Tijdens de volgende afspraak wordt de voorbereiding voor het gesprek met de bedrijfsarts besproken. Cliënt mag eerder bellen als het doorslapen erger wordt of het piekeren de overhand krijgt.`,
      },
    },
  },
  // somberheid met mantelzorg en medicatie
  {
    transcript: `POH: Goedemorgen, kom binnen. Hoe gaat het met u sinds de vorige keer?
CLIENT: Nou, wisselend eerlijk gezegd. De somberheid is er nog steeds, vooral 's ochtends. Ik kom moeilijk mijn bed uit.
POH: U zei de vorige keer dat het slapen ook lastig was. Hoe is dat nu?
CLIENT: Nog steeds slecht. Ik lig vaak tot twee uur wakker te piekeren. Over mijn werk, over mijn moeder die dementerend is. Mijn zus doet het meeste van de mantelzorg maar ik voel me er schuldig over.
POH: Uw zus vangt veel op, zegt u. Heeft u het daar samen wel eens over?
CLIENT: Weinig. Mijn zus woont dichtbij bij mijn moeder, ik niet. Mijn oom helpt ook af en toe, die doet de financiën van mijn moeder. Maar het voelt alsof ik tekortschiet.
POH: En de sertraline die de huisarts u heeft voorgeschreven, hoe gaat dat? U bent daar op [DATUM_1] mee gestart, 50 milligram.
CLIENT: Ik slik hem trouw elke ochtend. Ik had de eerste week wat misselijkheid maar dat is nu weg. Ik weet niet of ik al verschil merk.
POH: Dat kan ook nog komen, het middel heeft vier tot zes weken nodig. Ik heb u vandaag de vragenlijst laten invullen, de 4DKL. U scoort op distress zestien, dat is verhoogd, en op depressie een score van acht. Dat past bij wat u vertelt.
CLIENT: Ja, dat verbaast me niet.
POH: Zijn er ook dingen die energie geven?
CLIENT: We zijn net een week naar Spanje geweest met het gezin. Dat was echt even een verademing, daar sliep ik ook beter. Maar de eerste werkdag terug zat ik er meteen weer doorheen.
POH: Dat is een belangrijk signaal, dat het in Spanje beter ging. Dat zegt iets over de rol van werkdruk. Denkt u wel eens: het hoeft van mij allemaal niet meer?
CLIENT: Nee, zo somber ben ik niet. Geen nare gedachten in die richting, dat wil ik erbij zeggen.
POH: Goed dat u dat zo helder zegt. Over de sertraline: het voorschrijven en aanpassen daarvan loopt via de huisarts, ik koppel aan haar terug hoe het nu met u gaat en zij evalueert het middel met u. Van mijn kant wil ik u aanmelden voor de leefstijlgroep, en ik wil dat u met uw zus een gesprek plant over de verdeling van de zorg voor uw moeder. Zullen we dat afspreken?
CLIENT: Ja, dat is goed. Dat gesprek met mijn zus vind ik spannend maar ik snap dat het nodig is.
POH: Dan zie ik u op [DATUM_2] terug. Bel eerder als de klachten toenemen.`,
    summary: `Cliënt ervaart wisselende somberheid, vooral 's ochtends, en heeft moeite met opstaan. Slaapproblemen met piekeren over werk en de dementerende moeder. Voelt zich schuldig over de mantelzorg voor moeder, die voornamelijk door haar zus wordt gedaan, en voelt zich tekortschieten. Oom helpt met financiën. Gebruikt sinds [DATUM_1] 50 mg sertraline, ervaarde initiële misselijkheid, maar merkt nog geen effect. Een recente vakantie naar Spanje gaf verlichting en verbeterde slaap, maar de klachten keerden direct terug bij aanvang van het werk. De 4DKL-vragenlijst toont een verhoogde distressscore (16) en een depressiescore (8). Er zijn geen suïcidale gedachten. Het plan is om de sertraline-evaluatie via de huisarts te laten lopen, cliënt aan te melden voor een leefstijlgroep, en cliënt te stimuleren een gesprek met haar zus aan te gaan over de zorgverdeling voor hun moeder. Volgende afspraak op [DATUM_2].`,
    soep: {
      bondig: {
        S: `Cliënt rapporteert wisselende somberheid, vooral 's ochtends, en moeite met opstaan. Slaapt slecht door piekeren over werk en de dementerende moeder. Voelt zich schuldig over de mantelzorg, die voornamelijk door haar zus wordt gedaan, en voelt zich tekortschieten. Oom helpt met financiën van moeder. Gebruikt sinds [DATUM_1] 50 mg sertraline, ervaarde initiële misselijkheid, maar merkt nog geen effect. Een week vakantie in Spanje gaf verlichting en verbeterde slaap, maar klachten keerden terug bij aanvang werk. Geen suïcidale gedachten. Vindt gesprek met zus spannend.`,
        O: `De 4DKL-vragenlijst toont een distressscore van 16 (verhoogd) en een depressiescore van 8.`,
        E: `Er is sprake van verhoogde distress en depressieve klachten, passend bij de gerapporteerde symptomen. Werkdruk en mantelzorglast lijken bij te dragen aan de klachten. Geen suïcidaliteit.`,
        P: `POH koppelt terug aan huisarts over de situatie en sertralinegebruik; huisarts evalueert het middel met cliënt. Cliënt wordt aangemeld voor de leefstijlgroep. Cliënt plant een gesprek met haar zus over de verdeling van de zorg voor moeder. Volgende afspraak op [DATUM_2], eerder contact bij toename klachten.`,
      },
      steekwoorden: {
        S: `- Wisselende stemming, somberheid, vooral 's ochtends.
- Moeite met uit bed komen.
- Slecht slapen, vaak tot twee uur wakker piekeren.
- Piekeren over werk en dementerende moeder.
- Schuldgevoel over mantelzorg moeder, zus doet het meeste.
- Zus woont dichtbij moeder, cliënt niet.
- Oom helpt af en toe met financiën moeder.
- Gevoel tekort te schieten.
- Slikt sertraline 50 milligram trouw elke ochtend sinds [DATUM_1].
- Eerste week misselijkheid van sertraline, nu weg.
- Weet niet of er al verschil merkbaar is door sertraline.
- Niet verbaasd over 4DKL scores.
- Week in Spanje met gezin was verademing, sliep daar beter.
- Eerste werkdag terug meteen weer doorheen.
- Geen nare gedachten in de richting van 'het hoeft van mij allemaal niet meer'.
- Vindt gesprek met zus spannend maar snapt dat het nodig is.`,
        O: `- 4DKL vragenlijst ingevuld.
- Score 4DKL distress: zestien (verhoogd).
- Score 4DKL depressie: acht.`,
        E: `- Klachten passen bij 4DKL scores.
- Verhoogde distress en depressieve klachten.
- Werkdruk speelt een rol in klachten.`,
        P: `- POH koppelt terug aan huisarts over sertraline.
- Huisarts evalueert sertraline met cliënt.
- Aanmelding voor leefstijlgroep.
- Cliënt plant gesprek met zus over verdeling zorg moeder.
- Vervolgafspraak op [DATUM_2].
- Cliënt belt eerder bij toename klachten.`,
      },
      uitgebreid: {
        S: `Cliënt ervaart wisselende somberheid, vooral 's ochtends, en komt moeilijk uit bed. Het slapen is nog steeds slecht; cliënt ligt vaak tot twee uur wakker te piekeren over werk en de dementerende moeder. Er is een schuldgevoel over de mantelzorg voor moeder, die voornamelijk door de zus wordt gedaan, terwijl een oom de financiën regelt. Cliënt voelt zich tekortschieten. Slikt sertraline 50 mg trouw elke ochtend sinds [DATUM_1]; de initiële misselijkheid is verdwenen, maar cliënt merkt nog geen verschil. Een week vakantie naar Spanje met het gezin was een verademing en daar sliep cliënt beter, maar de klachten waren direct terug op de eerste werkdag. Cliënt geeft aan geen nare gedachten te hebben in de richting van 'het hoeft van mij allemaal niet meer'. Het gesprek met de zus over de zorgverdeling vindt cliënt spannend, maar snapt dat het nodig is.`,
        O: `Cliënt heeft de 4DKL vragenlijst ingevuld. De score op distress is zestien (verhoogd) en op depressie acht.`,
        E: `De gerapporteerde klachten van somberheid, slaapproblemen en piekeren passen bij de verhoogde distress- en depressiescores op de 4DKL. Het betere functioneren tijdens vakantie in Spanje en de snelle terugval bij werkhervatting suggereren een rol van werkdruk in de klachten. Er zijn geen suïcidale gedachten.`,
        P: `De POH koppelt de voortgang met sertraline terug aan de huisarts, die het middel met cliënt zal evalueren. Cliënt wordt aangemeld voor de leefstijlgroep. Cliënt plant een gesprek met de zus over de verdeling van de zorg voor moeder. Een vervolgafspraak staat gepland op [DATUM_2]. Cliënt belt eerder als de klachten toenemen.`,
      },
    },
  },
  // adolescent met ouder erbij
  {
    transcript: `POH: Welkom allebei. [NAAM_1], u bent vandaag met uw moeder gekomen.
CLIENT: Ja, mama wilde graag mee omdat ze zich zorgen maakt.
MOEDER: Ik zie hem steeds minder, hij komt zijn kamer bijna niet meer uit. En als zijn broer langskomt doet hij de deur niet open.
POH: [NAAM_1], herkent u dat beeld?
CLIENT: Deels. Ik ben gewoon moe. School lukt niet meer, ik ben twee keer blijven zitten met toetsen. En mijn broer en ik hebben ruzie, dus die wil ik gewoon niet zien. Dat heeft er niks mee te maken.
POH: Waar gaat de ruzie met uw broer over?
CLIENT: Hij vindt dat ik me aanstel. Mijn vader zegt dat ook. Alleen mama begrijpt het een beetje.
MOEDER: Zijn vader en ik zitten er verschillend in, dat klopt. Mijn man denkt dat het vanzelf overgaat.
POH: En gamen, u zei aan de telefoon dat dat veel tijd kost?
CLIENT: 's Nachts vooral. Tot een uur of vier. Dan slaap ik tot een uur of twaalf. Op schooldagen red ik dat niet dus dan meld ik me ziek.
POH: Hoe vaak bent u de afgelopen maand op school geweest?
CLIENT: Misschien de helft van de dagen.
MOEDER: Minder denk ik. De mentor heeft al twee keer gebeld.
POH: Ik hoor twee dingen: een verstoord dag-nachtritme met veel gamen, en spanning thuis, vooral met uw vader en broer. Ik zie ook dat u er vandaag vlak bij zit, weinig oogcontact maakt en zacht praat.
CLIENT: Ik vind dit soort gesprekken gewoon moeilijk.
POH: Dat mag ook. Mijn voorstel: ik meld u aan bij [ORGANISATIE_1] voor een intake, en tot die tijd spreken we een slaapschema af, geen schermen na middernacht. Mama, wilt u met uw man bespreken dat hij een keer meekomt?
MOEDER: Dat ga ik proberen. Mijn zus, de tante van [NAAM_1] dus, wil ook helpen, die is jeugdverpleegkundige geweest.
POH: Prima, alle steun is welkom. Dan plannen we het vervolg over twee weken.`,
    summary: `Cliënt [NAAM_1] kwam met zijn moeder, die zich zorgen maakt over zijn sociale terugtrekking en verminderde activiteit. Cliënt rapporteert vermoeidheid, problemen op school (twee keer blijven zitten, frequent ziek melden) en een verstoord dag-nachtritme door gamen tot 04:00 uur. Er is spanning thuis met zijn broer en vader, die zijn klachten bagatelliseren. De POH observeerde een vlakke presentatie, weinig oogcontact en zacht spreken. De werkhypothese is een verstoord dag-nachtritme door excessief gamen, leidend tot schoolverzuim en sociale terugtrekking, mede door spanningen in de thuissituatie. Het plan omvat een aanmelding bij [ORGANISATIE_1] voor een intake, een slaapschema zonder schermen na middernacht, en een vervolgconsult over twee weken. Moeder zal haar man vragen mee te komen en de tante, een jeugdverpleegkundige, heeft hulp aangeboden.`,
    soep: {
      bondig: {
        S: `Cliënt [NAAM_1] is moe, heeft moeite met school (twee keer blijven zitten met toetsen) en meldt zich ziek op schooldagen. Hij gamet 's nachts tot 04:00 uur en slaapt tot 12:00 uur. Er is ruzie met zijn broer en vader, die vinden dat hij zich aanstelt; zijn moeder begrijpt hem wel. Moeder maakt zich zorgen, ziet hem steeds minder, hij komt zijn kamer bijna niet meer uit en opent de deur niet voor zijn broer. Cliënt vindt dit soort gesprekken moeilijk. Mentor heeft twee keer gebeld.`,
        O: `Cliënt zit vlak, maakt weinig oogcontact en praat zacht.`,
        E: `Werkhypothese: verstoord dag-nachtritme door excessief gamen, leidend tot schoolverzuim en sociale terugtrekking, mede door spanningen in de thuissituatie.`,
        P: `Aanmelding bij [ORGANISATIE_1] voor een intake. Afspraak: slaapschema met geen schermen na middernacht. Moeder bespreekt met haar man dat hij meekomt naar een volgend consult. Vervolgconsult over twee weken. Tante (jeugdverpleegkundige) biedt hulp aan.`,
      },
      steekwoorden: {
        S: `- Klachten: moeheid, school lukt niet meer, twee keer blijven zitten met toetsen.
- Context: ruzie met broer, broer vindt dat client zich aanstelt. Vader denkt dat client zich aanstelt en dat het vanzelf overgaat. Moeder maakt zich zorgen, ziet client steeds minder, komt kamer bijna niet meer uit.
- Gedrag: gamet veel, vooral 's nachts tot 04:00 uur, slaapt tot 12:00 uur. Meldt zich ziek op schooldagen.
- Schoolbezoek: afgelopen maand misschien de helft van de dagen (client), minder (moeder). Mentor heeft twee keer gebeld.
- Beleving: vindt dit soort gesprekken moeilijk.
- Hulpbronnen: moeder wil met man bespreken of hij meekomt. Tante (zus van moeder, jeugdverpleegkundige geweest) wil helpen.`,
        O: `- Client zit vlak bij.
- Weinig oogcontact.
- Praat zacht.`,
        E: `- Werkhypothese: verstoord dag-nachtritme door veel gamen.
- Contextuele factoren: spanning thuis, met name met vader en broer.`,
        P: `- Aanmelding: intake bij [ORGANISATIE_1].
- Afspraak: slaapschema (geen schermen na middernacht).
- Actie moeder: bespreken met man of hij meekomt.
- Vervolg: consult over twee weken.`,
      },
      uitgebreid: {
        S: `Cliënt is gekomen met moeder, die zich zorgen maakt omdat cliënt steeds minder te zien is, bijna zijn kamer niet meer uitkomt en de deur niet opendoet voor zijn broer. Cliënt herkent dit deels en geeft aan moe te zijn. School lukt niet meer, cliënt is twee keer blijven zitten met toetsen. Er is ruzie met de broer, die cliënt niet wil zien. De broer en vader vinden dat cliënt zich aanstelt. Moeder begrijpt cliënt wel, maar vader denkt dat het vanzelf overgaat. Cliënt gamet veel, vooral 's nachts tot een uur of vier, en slaapt dan tot een uur of twaalf. Op schooldagen meldt cliënt zich ziek hierdoor. Cliënt schat de afgelopen maand de helft van de dagen op school te zijn geweest, moeder denkt minder en de mentor heeft al twee keer gebeld. Cliënt vindt dit soort gesprekken moeilijk.`,
        O: `Cliënt zit vlak bij, maakt weinig oogcontact en praat zacht.`,
        E: `Er is sprake van een verstoord dag-nachtritme door gamen en spanningen thuis, met name met vader en broer.`,
        P: `Cliënt wordt aangemeld bij [ORGANISATIE_1] voor een intake. Er is een slaapschema afgesproken: geen schermen na middernacht. Moeder zal met haar man bespreken of hij een keer meekomt. De zus van moeder (tante van [NAAM_1]), die jeugdverpleegkundige is geweest, wil ook helpen. Over twee weken is er een vervolgconsult gepland.`,
      },
    },
  },
  // overspanning, uitweidend consult
  {
    transcript: `POH: Vertel eens, wat brengt u vandaag hier?
CLIENT: Nou ja, waar zal ik beginnen. Het is eigenlijk al maanden zo dat ik mezelf niet ben. Mijn vrouw zei ook al, je bent jezelf niet. En op het werk zeggen ze het ook. Ik ben teamleider bij een logistiek bedrijf, we hebben een reorganisatie gehad, nou ja gehad, die loopt eigenlijk nog steeds, en sindsdien is het elke dag brandjes blussen. Elke dag. En dan kom ik thuis en dan is het eten en dan val ik op de bank in slaap. Mijn vrouw zegt dan, ga toch eens wat leuks doen, maar ik heb nergens puf voor.
POH: U noemt de reorganisatie. Wat is er voor u veranderd?
CLIENT: Ik heb er een team bij gekregen, dus ik stuur nu twee ploegen aan. En mijn eigen leidinggevende is vertrokken, er is een interim, en die wil alles anders. Alles moet in nieuwe systemen. Ik ben vijftig, ik leer dat allemaal wel, maar het kost me moeite en er is geen tijd voor. En ondertussen bellen de chauffeurs mij voor elk wissewasje, ook 's avonds, ook in het weekend. Vorige week zondag nog drie keer gebeld tijdens de verjaardag van mijn kleindochter. Mijn vrouw werd er boos om, niet op mij hoor, op de situatie, maar toch.
POH: En lichamelijk, hoe voelt u zich?
CLIENT: Gespannen. Hier, in mijn nek en schouders. Hoofdpijn ook, wel drie vier keer per week. En mijn hart slaat soms over, dat vind ik eng. De huisarts heeft laatst een hartfilmpje gemaakt en dat was goed, zei ze, dus het zit tussen de oren zeg maar. Nou ja, tussen de oren, het is spanning zei ze. En slapen gaat slecht, ik word om vijf uur wakker en dan maal ik door over het werk. Om een cijfer te geven, mijn energie is een 3-4 op een schaal van tien, terwijl ik altijd een acht was. Echt, ik was altijd degene met energie.
POH: U was altijd degene met energie, zegt u, en nu een drie tot vier. Hoe lang speelt dit al?
CLIENT: De reorganisatie begon vorig jaar. Maar de laatste twee drie maanden gaat het echt bergafwaarts. Mijn vrouw zag het het eerst. Ik dacht zelf, het gaat wel over na de vakantie, maar we zijn twee weken weggeweest en na twee dagen thuis was het weer precies hetzelfde. Dus het gaat niet vanzelf over, dat zie ik nu ook wel in.
POH: Ik hoor overbelasting die al langer duurt: aanhoudende werkstress, weinig herstel, lichamelijke spanningsklachten, vroeg wakker met piekeren, en de vakantie geeft geen herstel meer. Dat past bij overspanning. Het goede nieuws: uw hart is nagekeken en gezond, en u trekt nu aan de bel.
CLIENT: Overspanning, ja. Mijn broer heeft een burn-out gehad, die heeft er anderhalf jaar uit gelegen, daar ben ik wel bang voor.
POH: Dat is precies waarom we nu ingrijpen. Mijn voorstel: u gaat in gesprek met de bedrijfsarts over tijdelijke aanpassing van uw taken, we spreken een piekerkwartier en vaste ontspanningsmomenten af, u zet de werktelefoon na zeven uur 's avonds uit, en over 2-3 weken zie ik u terug. Als het dan niet kentert, kijken we verder, ook naar begeleiding bij [ORGANISATIE_1].
CLIENT: Dat telefoonpunt wordt lastig maar ik ga het doen. Mijn vrouw zal blij zijn dat ik geweest ben.`,
    summary: `De cliënt, een 50-jarige teamleider bij een logistiek bedrijf, ervaart al maanden dat hij zichzelf niet is, wat ook door zijn omgeving wordt opgemerkt. Dit hangt samen met een aanhoudende reorganisatie op zijn werk, waarbij hij meer verantwoordelijkheden heeft gekregen (twee teams aansturen) en moet wennen aan nieuwe systemen onder een interim-leidinggevende. Hij wordt ook buiten werktijd veelvuldig gebeld door chauffeurs, wat thuis voor spanningen zorgt. Thuis heeft hij nergens puf voor en valt hij snel in slaap. Lichamelijk uit dit zich in spanning in nek en schouders, frequente hoofdpijn en hartkloppingen (waarvoor een eerder hartfilmpje goed was). Hij slaapt slecht, wordt vroeg wakker en piekert over werk. Zijn energieniveau is significant gedaald. De klachten zijn de laatste 2-3 maanden verergerd en een recente vakantie bood geen duurzaam herstel. De cliënt is bang voor een burn-out, mede door de ervaring van zijn broer. De POH concludeert overspanning en stelt een plan voor dat gericht is op het verminderen van werkdruk en het bevorderen van herstel, inclusief een gesprek met de bedrijfsarts, het inlassen van een piekerkwartier en ontspanningsmomenten, en het uitzetten van de werktelefoon na werktijd. Een vervolgconsult staat gepland over 2-3 weken, met de optie voor verdere begeleiding bij [ORGANISATIE_1] indien nodig.`,
    soep: {
      bondig: {
        S: `Client rapporteert zich al maanden niet zichzelf te voelen, wat ook door zijn vrouw en collega's wordt opgemerkt. Hij is teamleider bij een logistiek bedrijf en ervaart aanhoudende werkstress door een reorganisatie, waarbij hij twee teams aanstuurt en te maken heeft met een interim-leidinggevende en nieuwe systemen. Chauffeurs bellen hem ook buiten werktijd, zelfs tijdens de verjaardag van zijn kleindochter, wat spanningen thuis veroorzaakt (vrouw werd boos op de situatie). Hij heeft nergens puf voor en valt thuis op de bank in slaap. Lichamelijk ervaart hij spanning in nek en schouders, hoofdpijn (3-4 keer per week) en hartkloppingen, waarvoor een hartfilmpje door de huisarts goed bevonden is. Hij slaapt slecht, wordt om vijf uur wakker en piekert over werk. Zijn energieniveau is gedaald van een 8 naar een 3-4. De klachten zijn de laatste 2-3 maanden verergerd en een vakantie bood geen herstel. Hij is bang voor een burn-out, gezien de ervaring van zijn broer. Het uitzetten van de werktelefoon na zeven uur 's avonds wordt als 'lastig' ervaren.`,
        O: `Geen aanvullende objectieve bevindingen.`,
        E: `Conclusie: overspanning, passend bij aanhoudende werkstress, weinig herstel, lichamelijke spanningsklachten en piekeren. Hart is nagekeken en gezond. Er is een risico op burn-out, vandaar de noodzaak tot ingrijpen.`,
        P: `Client gaat in gesprek met de bedrijfsarts over tijdelijke aanpassing van taken. Afspraken: piekerkwartier en vaste ontspanningsmomenten. Client zet de werktelefoon na zeven uur 's avonds uit. Vervolgconsult over 2-3 weken. Indien geen verbetering, wordt gekeken naar begeleiding bij [ORGANISATIE_1].`,
      },
      steekwoorden: {
        S: `- Zichzelf al maanden niet, vrouw en werk constateren dit ook. Teamleider bij logistiek bedrijf, lopende reorganisatie.
- Dagelijks 'brandjes blussen' op werk, thuis op bank in slaap vallen, nergens puf voor.
- Extra team erbij gekregen, stuurt nu twee ploegen aan. Nieuwe interim leidinggevende wil alles anders, nieuwe systemen.
- Moeite met leren nieuwe systemen op 50-jarige leeftijd, geen tijd. Chauffeurs bellen voor 'elk wissewasje', ook 's avonds en in weekend (bv. 3x tijdens verjaardag kleindochter). Vrouw boos om situatie.
- Lichamelijk: gespannen nek en schouders, hoofdpijn (3-4 keer/week). Hart slaat soms over, vindt dit eng. Huisarts heeft hartfilmpje gemaakt, was goed; huisarts zei dat het spanning was.
- Slaapt slecht, wordt om 5 uur wakker, maalt door over werk. Energie 3-4/10 (was altijd 8/10).
- Problematiek begon met reorganisatie vorig jaar, maar laatste 2-3 maanden echt bergafwaarts. Vrouw zag het het eerst. Vakantie gaf geen blijvend herstel.
- Bang voor burn-out, broer heeft er anderhalf jaar uit gelegen.
- Telefoon uitzetten na 19:00 wordt lastig, maar gaat het doen. Vrouw zal blij zijn met bezoek.`,
        O: `Geen aanvullende objectieve bevindingen.`,
        E: `- Overspanning.
- Hart nagekeken en gezond.
- Cliënt trekt aan de bel, ingrijpen nu mogelijk.`,
        P: `- Gesprek met bedrijfsarts over tijdelijke aanpassing taken.
- Afspraak piekerkwartier.
- Afspraak vaste ontspanningsmomenten.
- Werktelefoon na 19:00 uur uitzetten.
- Terugzien over 2-3 weken.
- Indien geen kentering, verder kijken, inclusief begeleiding bij [ORGANISATIE_1].`,
      },
      uitgebreid: {
        S: `Cliënt voelt zich al maanden niet zichzelf, wat ook wordt opgemerkt door zijn vrouw en collega's. Hij is teamleider bij een logistiek bedrijf waar een reorganisatie gaande is, wat leidt tot dagelijks "brandjes blussen". Thuis valt hij na het eten in slaap op de bank en heeft nergens puf voor, ondanks aanmoediging van zijn vrouw. Door de reorganisatie heeft hij een team erbij gekregen en stuurt nu twee ploegen aan. Zijn eigen leidinggevende is vertrokken en een interim leidinggevende wil alles anders, met veel nieuwe systemen. Als vijftigjarige kost het hem moeite om deze systemen te leren en er is geen tijd voor. Chauffeurs bellen hem voor elk wissewasje, ook 's avonds en in het weekend; vorige week zondag nog drie keer tijdens de verjaardag van zijn kleindochter, wat zijn vrouw boos maakte op de situatie. Lichamelijk voelt hij zich gespannen in nek en schouders, heeft 3-4 keer per week hoofdpijn en zijn hart slaat soms over, wat hij eng vindt. De huisarts heeft een hartfilmpje gemaakt dat goed was en zei dat het spanning is. Hij slaapt slecht, wordt om vijf uur wakker en piekert over het werk. Zijn energie is een 3-4 op een schaal van tien, terwijl hij altijd een acht was. Dit speelt al langer, maar de laatste twee-drie maanden gaat het echt bergafwaarts. Zijn vrouw zag het het eerst. Hij dacht dat het na de vakantie over zou gaan, maar na twee weken vakantie en twee dagen thuis was het weer precies hetzelfde. Hij ziet nu in dat het niet vanzelf overgaat. Hij is bang voor een burn-out, omdat zijn broer anderhalf jaar uit de roulatie is geweest met een burn-out. Hij verwacht dat zijn vrouw blij zal zijn dat hij geweest is.`,
        O: `Geen aanvullende objectieve bevindingen.`,
        E: `De klachten passen bij overspanning. Het hart is nagekeken en gezond bevonden. Er is een risico op burn-out, gezien de angst van de cliënt en de duur van de klachten.`,
        P: `Cliënt gaat in gesprek met de bedrijfsarts over tijdelijke aanpassing van zijn taken. Er wordt een piekerkwartier en vaste ontspanningsmomenten afgesproken. De cliënt zal de werktelefoon na zeven uur 's avonds uitzetten, ondanks dat hij dit lastig vindt. Over 2-3 weken volgt een terugkoppeling. Indien er dan geen verbetering is, wordt er gekeken naar begeleiding bij [ORGANISATIE_1].`,
      },
    },
  },
];

const SOEP_LABELS = {
  S: 'S — Subjectief',
  O: 'O — Objectief',
  E: 'E — Evaluatie',
  P: 'P — Plan',
};

const STATUS_LABEL: Record<SaraTaskStatus, string> = {
  pending: 'In wachtrij',
  transcribing: 'Transcriberen',
  pseudonymizing: 'Pseudonimiseren',
  summarizing: 'Samenvatten',
  pending_approval: 'Klaar voor controle',
  approved: 'Opgeslagen',
  failed: 'Mislukt',
};

// Volgorde van de server-side statussen; gebruikt om de pijplijn-stappen als
// done/actief/wachtend te tonen.
const STATUS_ORDER: SaraTaskStatus[] = [
  'pending',
  'transcribing',
  'pseudonymizing',
  'summarizing',
  'pending_approval',
];

const PIPELINE_STEPS: { status: SaraTaskStatus; label: string }[] = [
  { status: 'transcribing', label: 'Transcriberen' },
  { status: 'pseudonymizing', label: 'Pseudonimiseren' },
  { status: 'summarizing', label: 'Samenvatten' },
];

// Getimede duur per stap in de gesimuleerde test-pijplijn.
const TEST_STEP_MS = 1100;

type MilaMode = 'verdiepen' | 'werkvormen' | 'verwijzen' | 'plan';

const MILA_INTENT_SENTENCE: Record<Exclude<MilaMode, 'plan'>, string> = {
  verdiepen: 'Verdiep de theorie en richtlijnen bij het onderwerp van dit consult.',
  werkvormen: 'Geef concrete werkvormen en oefeningen bij het onderwerp van dit consult.',
  verwijzen:
    'Zoek passende zorgaanbieders in de Sociale Kaart om naar door te verwijzen op basis van dit consult.',
};

/** Bouwt de (gepseudonimiseerde) vraag die naar Mila gaat: consult-samenvatting
 *  + volledige SOEP + de gekozen intentie (ADR 0006 besluit 4/5). Geen ruw
 *  transcript. Mila verstuurt deze vraag direct bij binnenkomst (auto-send,
 *  besluit Jos 2026-07-23): de intentie is hier al gekozen en de SOEP is al
 *  gepseudonimiseerd, dus de aparte review-in-Mila verviel. */
/** S/O/E/P als platte tekst — één plek zodat het HIS-formaat niet uiteenloopt. */
function soepToText(soep: Soep): string {
  return `S: ${soep.S}\nO: ${soep.O}\nE: ${soep.E}\nP: ${soep.P}`;
}

// Plan-opzet krijgt ALLEEN het onderwerp dat de POH koos, geen SOEP of
// samenvatting: een opzet die op patiëntgegevens wordt afgestemd is een
// behandelbeslissing over die patiënt, en dat is MDR klasse IIa (toets
// 12 okt 2026). Een opzet per klachtenbeeld is kennis, net als een richtlijn.
function buildMilaQuestion(mode: MilaMode, soep: Soep, summary: string, onderwerp: string): string {
  if (mode === 'plan') return `Geef een algemene plan-opzet bij: ${onderwerp}`;
  const soepText = soepToText(soep);
  const summaryBlock = summary ? `Consult-samenvatting:\n${summary}\n\n` : '';
  return `${summaryBlock}SOEP-verslag van dit consult:\n${soepText}\n\n${MILA_INTENT_SENTENCE[mode]}`;
}

function fromApiSoep(s: NonNullable<SaraTaskView['soep']>): Soep {
  return { S: s.s, O: s.o, E: s.e, P: s.p };
}

function dutchSaraError(err: unknown, fallback = 'Er ging iets mis. Probeer het opnieuw.'): string {
  if (err instanceof ApiError) return err.message || fallback;
  return err instanceof Error && err.message ? err.message : fallback;
}

function fmt(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// De detectie zelf (inclusief de iPad-die-zich-als-Mac-voordoet) zit in
// lib/sara.ts, zodat ze zonder DOM te testen is.
//
// Productbesluit 2026-08-10: opnemen op telefoon en tablet mag weer. Het stond
// sinds 6 aug 2026 dicht omdat een opname stil kon mislukken zodra het scherm
// vergrendelde of de app naar de achtergrond ging. De grens is echt en blijft:
// een webapp kan op iOS niet doorgaan met opnemen als de gebruiker naar een
// andere app schakelt. In plaats van weigeren doen we nu twee dingen: vooraf
// eerlijk uitleggen wat de gebruiker moet doen (de voorlichting hieronder), en
// een onderbreking luid melden in plaats van hem stil te laten passeren (zie
// de gatendetectie in `noteAudioGap`). IS_MOBILE stuurt alleen die voorlichting
// en de toon van de melding, niet meer de toegang.
const IS_MOBILE =
  typeof navigator !== 'undefined' &&
  isMobileDevice(navigator.userAgent, navigator.maxTouchPoints ?? 0);

const UNSUPPORTED_BROWSER_MSG =
  'Opnemen wordt in deze browser niet ondersteund. Gebruik Safari, Chrome of Edge.';

// Timeslice van MediaRecorder: elke 5 seconden een chunk naar IndexedDB.
const TIMESLICE_MS = 5000;

/**
 * Vanaf welk gat tussen twee chunks we van een onderbreking spreken. Gelijk aan
 * de waakhond hieronder (drie gemiste chunks), zodat wat de POH tijdens de
 * opname als waarschuwing ziet exact hetzelfde is als wat er na afloop geteld
 * wordt. Ruim boven de timeslice, want een enkele late chunk door een trage
 * schijf of een drukke tab is geen onderbreking.
 */
const AUDIO_GAP_MS = 15_000;

/** Uitleg bij een zeker lege opname. De waarschijnlijke oorzaak verschilt: een
 *  korte opname is meestal per ongeluk gestopt, een lange is door de browser
 *  gepauzeerd. */
function emptyRecordingMessage(bytes: number, durationSec: number): string {
  const kb = Math.round(bytes / 1024);
  if (durationSec < 10) {
    return `De opname van ${fmt(durationSec)} is te kort en bevat vrijwel geen audio (${kb} kB). Er is niets verwerkt.`;
  }
  return (
    `De opname van ${fmt(durationSec)} bevat vrijwel geen audio (${kb} kB) en is niet verwerkt. ` +
    'Dit gebeurt als het scherm vergrendelt of Sara tijdens het consult naar de achtergrond gaat. ' +
    'Neem het consult opnieuw op en houd Sara daarbij zichtbaar op het scherm.'
  );
}

/**
 * Uitleg bij een opname die onderweg onderbroken is geweest. Dit is geen
 * schatting maar een meting: tussen twee chunks zat een gat, dus die tijd is
 * niet opgenomen. De POH moet dat weten vóór hij het verslag vertrouwt, ook als
 * de rest van de opname er prima uitziet en de pijplijn gewoon een SOEP oplevert.
 */
function gapMessage(count: number, gapSec: number): string {
  const keer = count === 1 ? 'één keer' : `${count} keer`;
  return (
    `De opname is ${keer} onderbroken, samen ongeveer ${fmt(gapSec)}. Dat deel van het consult ` +
    'is niet opgenomen en ontbreekt dus in het transcript en het verslag. Controleer het verslag ' +
    'extra goed en vul zelf aan wat er mist.'
  );
}

/** Uitleg bij een opname die te dun is voor de gemelde duur. Dit is een
 *  schatting, dus de tekst noemt wat er gemeten is en laat de POH zelf
 *  beslissen: hij weet of het consult werkelijk zo lang duurde. */
function thinRecordingMessage(bytes: number, durationSec: number): string {
  const kb = Math.round(bytes / 1024);
  if (durationSec < MIN_RATE_CHECK_SEC) {
    // Bij een korte opname is "weinig audio" geen bewijs van een storing: een
    // bewust ingesproken aanvulling van een paar seconden ziet er precies zo
    // uit. De tekst mag hier dus geen oorzaak beweren, alleen melden wat er
    // gemeten is en de weg vooruit wijzen.
    return (
      `De opname duurt ${fmt(durationSec)} en bevat ${kb} kB audio. Dat is weinig, dus mogelijk is ` +
      'de opname eerder gestopt dan je bedoelde. Was dit een korte, bewuste opname, dan klopt het ' +
      'en kun je hem gewoon laten verwerken. De opname is bewaard.'
    );
  }
  return (
    `De opname duurde ${fmt(durationSec)}, maar er is maar ${kb} kB audio opgeslagen. Dat is veel ` +
    'minder dan bij een gesprek van deze lengte te verwachten is. Waarschijnlijk heeft de browser ' +
    'de opname onderweg gepauzeerd, bijvoorbeeld doordat Sara naar de achtergrond ging of het ' +
    'scherm vergrendelde, en bevat de opname alleen het begin van het consult. De opname is ' +
    'bewaard. Je kunt hem alsnog laten verwerken; het verslag is dan mogelijk onvolledig.'
  );
}

function stepIndex(phase: Phase): number {
  if (phase === 'processing' || phase === 'test-processing') return 1;
  if (phase === 'review' || phase === 'saved' || phase === 'test-review') return 2;
  return 0;
}

function StepDot({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  return (
    <div
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${
        done
          ? 'bg-brand-500 text-white'
          : active
            ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-500'
            : 'bg-brand-50 text-ink-900/40'
      }`}
    >
      {done ? <CheckIcon size={12} strokeWidth={2.5} /> : n}
    </div>
  );
}

// Samenvatting voor de Mila-overdracht, standaard ingeklapt (Kimberley 19-8:
// de review bevat al veel tekst): de POH ziet en corrigeert wat er wordt
// doorgegeven (hard rule 3, mens keurt goed) zonder extra leeslast.
function SummaryCard({
  value,
  onChange,
  readOnly,
}: {
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <details className="rounded-lg border border-brand-100 bg-white">
      <summary className="cursor-pointer list-none px-4 py-4 sm:px-5 [&::-webkit-details-marker]:hidden">
        <h2 className="text-[13px] font-semibold text-ink-900">Samenvatting voor overdracht</h2>
        <p className="mt-0.5 text-[11px] text-ink-900/50">
          Gaat mee bij de overdracht naar Mila. Klik om te lezen of te bewerken.
        </p>
      </summary>
      <div className="px-4 pb-4 sm:px-5">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          rows={5}
          className="max-h-[45vh] w-full resize-y rounded-md border border-brand-100 bg-white px-3 py-2.5 text-[13px] leading-relaxed text-ink-900 [field-sizing:content] read-only:bg-[#f7fafa] read-only:text-ink-900/80 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
    </details>
  );
}

// Voettekst van de SOEP-kaart: verslag opnieuw genereren in een (andere)
// stijl als de POH niet tevreden is (verzoek Jos 20-8-2026). Zelfde control
// in de echte review (servercall op het bewaarde transcript) en de demo
// (client-side wissel uit de voorgegenereerde varianten).
function RegenerateRow({
  soepStyle,
  onStyleChange,
  onRegenerate,
}: {
  soepStyle: SaraSoepStyle;
  onStyleChange: (v: SaraSoepStyle) => void;
  onRegenerate: () => void;
}) {
  return (
    <div className="border-t border-brand-50 px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-[200px] flex-1 text-[11px] text-ink-900/50">
          Niet tevreden? Genereer het verslag opnieuw, eventueel in een andere stijl. De huidige
          velden worden dan overschreven.
        </span>
        <select
          value={soepStyle}
          onChange={(e) => onStyleChange(e.target.value as SaraSoepStyle)}
          aria-label="SOEP-stijl voor opnieuw genereren"
          className="rounded-md border border-brand-100 bg-white px-2 py-1.5 text-[12px] text-ink-900"
        >
          <option value="steekwoorden">Steekwoorden</option>
          <option value="bondig">Bondig</option>
          <option value="uitgebreid">Uitgebreid</option>
        </select>
        <button
          type="button"
          onClick={onRegenerate}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-4 py-2 text-[12px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900 active:scale-95"
        >
          <RotateCcwIcon size={13} strokeWidth={2} />
          Genereer opnieuw
        </button>
      </div>
    </div>
  );
}

// Afronden met twee-staps bevestiging: "Verwerkt in HIS" → verslag en opname
// definitief weg. Gedeeld door de echte review (servertaak wordt verwijderd)
// en de demo/test-review (client-side, zelfde gedrag en melding).
function HisRow({
  confirming,
  busy,
  onStart,
  onCancel,
  onConfirm,
}: {
  confirming: boolean;
  busy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="border-t border-brand-50 px-4 py-4 sm:px-5">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex-1 text-[12px] text-ink-900/70">
            De opname, het transcript en het SOEP-verslag worden definitief verwijderd.
          </span>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-error px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-red-700 active:scale-95 disabled:opacity-50"
          >
            {busy ? (
              <Loader2Icon size={14} className="animate-spin" strokeWidth={2} />
            ) : (
              <CheckIcon size={14} strokeWidth={2.5} />
            )}
            Ja, verwerkt en verwijderen
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-brand-100 bg-white px-4 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900 disabled:opacity-50"
          >
            Annuleer
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-ink-900/50">
            Klaar met overnemen? De opname wordt hierna direct verwijderd.
          </span>
          <button
            type="button"
            onClick={onStart}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 active:scale-95"
          >
            <CheckIcon size={14} strokeWidth={2.5} />
            Verwerkt in HIS
          </button>
        </div>
      )}
    </div>
  );
}

function StepBar({ idx }: { idx: number }) {
  const steps = ['Opnemen', 'Transcript', 'SOEP'];
  return (
    <div className="flex items-center gap-2">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-1.5">
          <StepDot n={i + 1} active={i === idx} done={i < idx} />
          <span
            className={`hidden text-[12px] font-medium sm:block ${
              i === idx ? 'text-ink-900' : i < idx ? 'text-brand-600' : 'text-ink-900/40'
            }`}
          >
            {label}
          </span>
          {i < steps.length - 1 && (
            <div
              className={`ml-1.5 h-px w-6 shrink-0 ${i < idx ? 'bg-brand-400' : 'bg-brand-100'}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Waveform() {
  const heights = [14, 22, 30, 18, 26, 34, 20, 28, 16, 24, 32, 18];
  return (
    <div className="flex items-center gap-[3px]">
      {heights.map((h, i) => (
        <div
          key={i}
          className="w-1 rounded-full bg-brand-400"
          style={{
            height: `${h}px`,
            animation: `sarabar 0.9s ease-in-out ${i * 0.07}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}

// Rendert de drie pijplijnstappen met done/actief/wachtend-markering. Gedeeld
// door de echte pijplijn (voortgang uit taskStatus) en de test-pijplijn
// (voortgang uit een stap-index).
function PipelineProgress({ current }: { current: number }): JSX.Element {
  return (
    <ol className="space-y-3">
      {PIPELINE_STEPS.map((step, i) => {
        const done = current > i;
        const active = current === i;
        return (
          <li key={step.status} className="flex items-center gap-3">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                done
                  ? 'bg-brand-500 text-white'
                  : active
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-brand-50 text-ink-900/40'
              }`}
            >
              {done ? (
                <CheckIcon size={12} strokeWidth={2.5} />
              ) : active ? (
                <Loader2Icon size={12} className="animate-spin" strokeWidth={2.5} />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </span>
            <span
              className={`text-[13px] ${active ? 'font-semibold text-ink-900' : 'text-ink-900/60'}`}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// Eerste regel van de E als voorstel voor het Plan-opzet-onderwerp.
function eersteRegel(text: string): string {
  return (text.split('\n').find((l) => l.trim()) ?? '').trim().slice(0, 120);
}

// Mila-antwoord dat via "Terug naar Sara" binnenkomt. Staat NAAST het verslag,
// niet in de P: kennis uit de kennisbank is geen afspraak met de patiënt, en
// het in het dossier schuiven maakt Mila tot een beslissing over die patiënt
// (MDR-toets 12 okt 2026). Overnemen is een eigen keuze van de POH.
function KennisCard({ text }: { text: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-ring2-border bg-white px-4 py-4 sm:px-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-ink-900">Uit de kennisbank (Mila)</h2>
        <CopyFieldButton text={text} />
      </div>
      <p className="mt-0.5 text-[11px] text-ink-900/50">
        Achtergrond bij dit consult, geen onderdeel van het verslag. Wat je overneemt, kies je zelf.
      </p>
      <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-900">{text}</p>
    </div>
  );
}

// Overdracht-knoppenrij naar Mila; gedeeld door het opnamepad en de test-modus
// zodat beide exact dezelfde vier intenties aanbieden. Plan-opzet vraagt eerst
// het onderwerp; voorgevuld met de E, die sinds 12 okt alleen bevat wat de POH
// zelf in het gesprek benoemde.
function MilaHandoff({
  onOpen,
  onVerwijsbrief,
  defaultOnderwerp,
}: {
  onOpen: (mode: MilaMode, onderwerp?: string) => void;
  /** Alleen aanwezig als de verwijsbrief-functie aan staat (ADR 0017). */
  onVerwijsbrief?: () => Promise<void>;
  defaultOnderwerp: string;
}): JSX.Element {
  const [planOpen, setPlanOpen] = useState(false);
  const [onderwerp, setOnderwerp] = useState('');
  const [briefFout, setBriefFout] = useState(false);
  const [briefBezig, setBriefBezig] = useState(false);
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {onVerwijsbrief && (
          <button
            type="button"
            disabled={briefBezig}
            onClick={() => {
              setBriefFout(false);
              setBriefBezig(true);
              onVerwijsbrief().catch(() => {
                setBriefBezig(false);
                setBriefFout(true);
              });
            }}
            title="Mila schrijft uit dit consult een concept-verwijsbrief om in het HIS te plakken"
            className="inline-flex items-center gap-1.5 rounded-xl border border-brand-500 bg-brand-50 px-3 py-1.5 text-[12px] font-semibold text-brand-800 transition-colors hover:bg-brand-100 disabled:opacity-60"
          >
            <FilePlusIcon size={13} />
            {briefBezig
              ? 'Brief wordt geschreven…'
              : briefFout
                ? 'Mislukt, probeer opnieuw'
                : 'Maak verwijsbrief aan'}
          </button>
        )}
        <span className="text-[11px] text-ink-900/50">Vraag Mila:</span>
        <button
          type="button"
          onClick={() => onOpen('verdiepen')}
          title="Mila zoekt verdiepende theorie en richtlijnen bij het onderwerp van dit consult"
          className="inline-flex items-center gap-1.5 rounded-xl border border-ring2-border bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900 transition-colors hover:border-brand-700 hover:text-brand-800"
        >
          <BookOpenIcon size={13} />
          Verdiepen
        </button>
        <button
          type="button"
          onClick={() => onOpen('werkvormen')}
          title="Mila zoekt concrete werkvormen en oefeningen bij het onderwerp van dit consult"
          className="inline-flex items-center gap-1.5 rounded-xl border border-ring2-border bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900 transition-colors hover:border-brand-700 hover:text-brand-800"
        >
          <ClipboardListIcon size={13} />
          Werkvormen
        </button>
        <button
          type="button"
          onClick={() => onOpen('verwijzen')}
          title="Mila zoekt passende zorgaanbieders in de Sociale Kaart"
          className="inline-flex items-center gap-1.5 rounded-xl border border-ring2-border bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900 transition-colors hover:border-brand-700 hover:text-brand-800"
        >
          <MapPinIcon size={13} />
          Verwijzen
        </button>
        <button
          type="button"
          onClick={() => {
            setOnderwerp(defaultOnderwerp);
            setPlanOpen((v) => !v);
          }}
          title="Mila geeft een algemene opzet bij een klachtenbeeld dat jij kiest; het plan voor je cliënt maak je zelf"
          className="inline-flex items-center gap-1.5 rounded-xl border border-ring2-border bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900 transition-colors hover:border-brand-700 hover:text-brand-800"
        >
          <ListChecksIcon size={13} />
          Plan-opzet
        </button>
      </div>
      {planOpen && (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (onderwerp.trim()) onOpen('plan', onderwerp.trim());
          }}
        >
          <label htmlFor="plan-onderwerp" className="text-[11px] text-ink-900/50">
            Plan-opzet bij:
          </label>
          <input
            id="plan-onderwerp"
            value={onderwerp}
            onChange={(e) => setOnderwerp(e.target.value)}
            placeholder="Klachtenbeeld, bijv. piekeren en slaapproblemen"
            maxLength={120}
            className="min-w-[16rem] flex-1 rounded-md border border-brand-100 bg-white px-2 py-1 text-[12px] text-ink-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={!onderwerp.trim()}
            className="rounded-xl bg-brand-700 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-800 disabled:opacity-40"
          >
            Vraag Mila
          </button>
          <span className="basis-full text-[11px] text-ink-900/50">
            Alleen het onderwerp gaat naar Mila, geen verslag. Noem geen persoonsgegevens.
          </span>
        </form>
      )}
    </>
  );
}

export function SaraPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Demo-omgeving (issue #43): alleen de gescripte voorbeeldopname — geen
  // microfoon, geen upload, geen pijplijn. De bestaande test-workflow is het
  // demopad; de echte workflow verdwijnt uit beeld.
  const { user } = useAuth();
  const demo = user?.demo === true;
  const [phase, setPhase] = useState<Phase>('idle');
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [soep, setSoep] = useState<Soep>(EMPTY_SOEP);
  const [summary, setSummary] = useState('');
  const [kennis, setKennis] = useState('');
  const [metrics, setMetrics] = useState<SaraConversationMetrics | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [taskStatus, setTaskStatus] = useState<SaraTaskStatus | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [backgrounded, setBackgrounded] = useState(false);
  // Er komen geen MediaRecorder-chunks meer binnen (scherm vergrendeld,
  // tabblad geparkeerd op een telefoon): waarschuwen tijdens de opname zelf.
  const [stalled, setStalled] = useState(false);
  // Gemeten onderbrekingen: hoe vaak er een gat in de audio zat en hoeveel
  // seconden daarmee gemoeid waren. Blijft na het stoppen staan tot `reset()`,
  // want juist ná de opname moet de POH weten dat er stukken ontbreken.
  const [gapCount, setGapCount] = useState(0);
  const [gapSec, setGapSec] = useState(0);
  const [recovery, setRecovery] = useState<BufferedRecording | null>(null);
  // Wat er met de audio van déze mislukking is gebeurd. Bepaalt welke knoppen
  // en welke bewaartermijn-melding het foutpaneel toont:
  //   'none':      de audio staat serverside (bucket, 24u); niets lokaals.
  //   'retryable': de blob staat nog in lastBlobRef en mag opnieuw geüpload.
  //   'thin':      idem, maar de plausibiliteitscontrole vond hem te dun; de
  //                opname is bewaard, niet gewist, want die controle is een
  //                schatting en de POH mag hem alsnog doorzetten.
  //   'discarded': lege opname, niets geüpload, buffer gewist, niets te redden.
  // Eén waarde in plaats van drie losse vlaggen, want ze sluiten elkaar uit en
  // mogen elkaar dus nooit tegenspreken.
  const [failedAudio, setFailedAudio] = useState<FailedAudio>('none');
  const [recentTasks, setRecentTasks] = useState<SaraTaskListItem[]>([]);
  // Twee-staps wisbevestiging in de lijst (geen window.confirm: blokkeert de tab).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // "Verwerkt in HIS"-flow: twee-staps bevestiging + melding na afloop.
  const [hisConfirm, setHisConfirm] = useState(false);
  const [hisBusy, setHisBusy] = useState(false);
  const [hisNotice, setHisNotice] = useState(false);
  // SOEP-schrijfstijl (besluit 20-8-2026): voorkeur op het gebruikersdocument;
  // de backend stempelt hem bij de volgende opname op de taak.
  const [soepStyle, setSoepStyle] = useState<SaraSoepStyle>('bondig');
  // "Verwerk op de achtergrond" (feedback Marc 19-8-2026): melding op de
  // idle-staat dat de vorige opname doorloopt en onder Recente opnames komt.
  const [bgNotice, setBgNotice] = useState(false);
  // Test-/demo-modus.
  const [testStep, setTestStep] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  // Of het schermslot op dít moment nog gewenst is. WAAROM naast de sentinel:
  // de aanvraag wordt bewust niet afgewacht (hij moet binnen de klik starten,
  // vóór getUserMedia), dus elke afbreking van het opstarten daarna draait
  // releaseWakeLock terwijl de aanvraag nog onderweg is. De sentinel is dan nog
  // null, er valt niets vrij te geven, en even later komt het slot alsnog
  // binnen: een telefoon die "Geen toegang tot de microfoon" toont en het scherm
  // oneindig aanhoudt. Deze vlag laat de late sentinel zichzelf opruimen.
  const wakeLockWantedRef = useRef(false);
  const chunksRef = useRef<Blob[]>([]);
  const lastChunkAtRef = useRef<number>(0);
  const gapMsRef = useRef<number>(0);
  const gapCountRef = useRef<number>(0);
  const startedAtRef = useRef<number>(0);
  const taskIdRef = useRef<string | null>(null);
  const pollAbortRef = useRef<boolean>(false);
  const testAbortRef = useRef<boolean>(false);
  const lastBlobRef = useRef<Blob | null>(null);
  const lastDurationRef = useRef<number>(0);

  // Terugweg vanuit Mila: de POH klikt daar "Terug naar Sara" en komt hier
  // terug in de review-stand. Mila's antwoord staat in een eigen blok naast
  // het verslag (KennisCard), nooit in de P. Runt eenmalig op mount; wist de router-state zodat
  // een terug-navigatie niet opnieuw vult.
  useEffect(() => {
    const st = location.state as {
      returnedSoep?: Soep;
      returnedSummary?: string;
      returnedTaskId?: string | null;
      returnedKennis?: string;
    } | null;
    if (st?.returnedSoep) {
      setSoep(st.returnedSoep);
      setKennis(st.returnedKennis ?? '');
      if (st.returnedSummary) setSummary(st.returnedSummary);
      taskIdRef.current = st.returnedTaskId ?? null;
      setPhase('review');
      window.history.replaceState({}, '');
    }
    // Mount-only: leest de terug-navigatie eenmalig; deps bewust leeg.
  }, []);

  // Chunk-waakhond: timeslice is 5s, dus 15s zonder chunk betekent dat de
  // browser de opname heeft gepauzeerd. De POH ziet dat anders pas na afloop,
  // als het consult al verloren is (feedback Marc, 30 jul 2026).
  useEffect(() => {
    if (phase !== 'recording') return;
    const id = setInterval(() => {
      // Alleen waarschuwen zodra de browser heeft laten zien dát hij tijdens de
      // opname chunks levert. Een browser die de timeslice negeert levert er nul
      // tot aan stop(); zonder deze voorwaarde zou een volledig geslaagde opname
      // daar vanaf de vijftiende seconde als onderbroken op het scherm staan.
      const chunkGeleverd = chunksRef.current.length > 0;
      setStalled(chunkGeleverd && Date.now() - lastChunkAtRef.current > AUDIO_GAP_MS);
    }, 5000);
    return () => {
      clearInterval(id);
      setStalled(false);
    };
  }, [phase]);

  // Opnametimer (echte én test-opname; de duur wordt uit startedAtRef berekend
  // zodat achtergrond-suspensie de duur niet vervormt).
  useEffect(() => {
    if (phase !== 'recording' && phase !== 'test-recording') return;
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  /**
   * Meet het gat sinds de vorige chunk en telt het als onderbreking zodra het
   * groter is dan AUDIO_GAP_MS.
   *
   * WAAROM aan de chunks meten en niet aan visibilitychange: zodra de browser
   * de opname pauzeert staat de hele pagina stil, dus een teller die zelf moet
   * tikken telt precies de onderbreking niet mee die hij moet meten. De
   * tussenpozen van MediaRecorder overleven dat wél; ze worden achteraf
   * zichtbaar zodra de volgende chunk of de stop binnenkomt. En het werkt voor
   * elke oorzaak (achtergrond, schermvergrendeling, inkomend gesprek, een
   * microfoon die door een andere app is ingepikt) zonder die te hoeven kennen.
   *
   * De timeslice gaat eraf: een normale chunk komt vijf seconden na de vorige
   * en die vijf seconden audio zijn niet kwijt. Wat overblijft is de ontbrekende
   * tijd, liever iets te laag geschat dan te hoog.
   *
   * De meting staat of valt met een browser die de timeslice honoreert; wat er
   * gebeurt als hij dat niet doet, staat bij de wipe in `recorder.onstop`.
   */
  const noteAudioGap = useCallback((now: number) => {
    const gap = now - lastChunkAtRef.current;
    if (gap <= AUDIO_GAP_MS) return;
    gapMsRef.current += gap - TIMESLICE_MS;
    gapCountRef.current += 1;
    setGapCount(gapCountRef.current);
    setGapSec(Math.round(gapMsRef.current / 1000));
  }, []);

  const resetGaps = useCallback(() => {
    gapMsRef.current = 0;
    gapCountRef.current = 0;
    setGapCount(0);
    setGapSec(0);
  }, []);

  const releaseWakeLock = useCallback(() => {
    wakeLockWantedRef.current = false;
    void wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
  }, []);

  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    wakeLockWantedRef.current = true;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      if (!wakeLockWantedRef.current) {
        // Tussen de aanvraag en het antwoord is het slot alweer opgezegd (zie
        // wakeLockWantedRef). Zonder deze poort belandt de sentinel alsnog in
        // de ref en blijft het scherm eindeloos aan.
        void sentinel.release().catch(() => {});
        return;
      }
      wakeLockRef.current = sentinel;
    } catch {
      /* geweigerd of niet beschikbaar — de opname loopt gewoon door */
    }
  }, []);

  // Onbreekbescherming: waarschuw bij tab-verlaten zolang er een opname of
  // niet-geüploade audio is (ADR 0009 punt 3).
  useEffect(() => {
    const dirty = phase === 'recording' || phase === 'uploading' || phase === 'recovery';
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // visibilitychange: de Wake Lock wordt door de browser vrijgegeven zodra de
  // pagina verborgen is en komt niet vanzelf terug, dus vraag hem opnieuw aan
  // bij terugkeer. Op een computer loopt de opname ondertussen door; op mobiel
  // kan hij stilvallen, en of dat gebeurd is bepaalt niet dit event maar de
  // gatenmeting op de chunks (`noteAudioGap`).
  useEffect(() => {
    const onVis = () => {
      if (phase !== 'recording') return;
      if (document.visibilityState === 'visible') {
        setBackgrounded(false);
        void acquireWakeLock();
      } else {
        setBackgrounded(true);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [phase, acquireWakeLock]);

  // Stijlvoorkeur laden; fouten laten de default (bondig) staan.
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    void apiGet<{ user: { soepStyle?: SaraSoepStyle } }>('/api/v1/profile/me')
      .then((r) => {
        if (!cancelled && r.user.soepStyle) setSoepStyle(r.user.soepStyle);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [demo]);

  const changeSoepStyle = (value: SaraSoepStyle): void => {
    setSoepStyle(value);
    // Best-effort opslaan; mislukt de write, dan wint de serverwaarde bij de
    // volgende paginalaad vanzelf weer.
    void apiPut('/api/v1/profile', { soepStyle: value }).catch(() => {});
  };

  // Recente opnames (72u-retentie) ophalen zodra de pagina in de lege staat
  // staat, zodat een POH een eerdere taak kan terugvinden na een refresh.
  // Fouten (flag uit, netwerk) laten de lijst gewoon leeg.
  useEffect(() => {
    if (phase !== 'idle') return;
    let cancelled = false;
    void fetchSaraTasks()
      .then((r) => {
        if (!cancelled) setRecentTasks(r.tasks);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [phase]);

  // Bij binnenkomst: staat er een achtergebleven opname in IndexedDB? Bied
  // herstel aan.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const buf = await bufferLoad();
        if (cancelled || !buf) return;
        // Hier zat de lus waarvoor het wissen ooit is toegevoegd: een buffer
        // zonder bruikbare audio werd bij elke paginalaad opnieuw aangeboden.
        // Die knippen we bij de bron door, en alleen voor de twee gevallen
        // waarin het zeker is: een lege buffer ('empty') en een misklik van
        // een seconde ('negligible') verdwijnen stil, alles wat 'thin' of 'ok'
        // is blijft staan en wordt aangeboden. Zo rust de opruiming nooit op
        // een schatting en verliest een korte, bewuste opname niets.
        const check = classifyRecording(buf.blob.size, buf.meta.durationSec);
        if (check === 'empty' || check === 'negligible') {
          await bufferClear().catch(() => {});
          return;
        }
        setRecovery(buf);
        setPhase('recovery');
      } catch {
        /* buffer onleesbaar — negeer, verse start */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Opruimen bij unmount.
  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
      testAbortRef.current = true;
      releaseWakeLock();
      stopStream();
    };
  }, [releaseWakeLock, stopStream]);

  // De enige route naar de foutstaat. WAAROM: het foutpaneel praat over de
  // audio van déze poging (welke knoppen, welke bewaartermijn-melding). Zolang
  // elke aanroeper die vlaggen zelf zette, bleef de uitkomst van een vórige
  // opname staan bij elke overgang die het vergat: een heropende taak die niet
  // laadde beweerde dan "de opname is niet bewaard", en "Toch uploaden en
  // verwerken" bood de vorige opname aan. Door de audiostand hier verplicht bij
  // de fout te zetten, met 'none' als default, valt vergeten de veilige kant op.
  const fail = useCallback((message: string, audio: FailedAudio = 'none') => {
    setErrorMsg(message);
    setFailedAudio(audio);
    setPhase('failed');
  }, []);

  // Past een terminale taakstatus toe op de UI-state; geeft terug of de taak
  // terminaal was. Gedeeld door pollTask en het heropenen vanuit de lijst;
  // `audio` zegt wat het foutpaneel over lokale audio mag beweren als deze taak
  // mislukt is (na een eigen upload staat de blob er nog, na het heropenen van
  // een taak uit de lijst niet).
  const applyTask = useCallback(
    (task: SaraTaskView, audio: FailedAudio): boolean => {
      if (task.status === 'pending_approval') {
        setTranscript(task.transcriptPseudonymized ?? '');
        if (task.soep) setSoep(fromApiSoep(task.soep));
        setSummary(task.summary ?? '');
        setMetrics(task.conversationMetrics ?? null);
        setPhase('review');
        return true;
      }
      if (task.status === 'approved') {
        setTranscript(task.transcriptPseudonymized ?? '');
        if (task.soep) setSoep(fromApiSoep(task.soep));
        setSummary(task.summary ?? '');
        setMetrics(task.conversationMetrics ?? null);
        setPhase('saved');
        return true;
      }
      if (task.status === 'failed') {
        fail(task.error?.message || 'De verwerking is mislukt.', audio);
        return true;
      }
      return false;
    },
    [fail],
  );

  const pollTask = useCallback(
    async (taskId: string, audio: FailedAudio) => {
      pollAbortRef.current = false;
      const start = Date.now();
      // Ruim boven de realistische doorlooptijd (GPU-cold-start + wachtrij +
      // 60-min consult): eerder opgeven maakt een serverside afgeronde taak
      // onvindbaar en lokt dubbele verwerking uit.
      const MAX_MS = 60 * 60 * 1000;
      // Na de eerste 2 minuten trager pollen: 3s-polls van een lang consult
      // zouden anders bijna de hele globale rate-limit (300/15min) opeten.
      const FAST_WINDOW_MS = 2 * 60 * 1000;
      let consecutiveErrors = 0;
      while (!pollAbortRef.current && Date.now() - start < MAX_MS) {
        const interval = Date.now() - start < FAST_WINDOW_MS ? 3000 : 10_000;
        await new Promise((r) => setTimeout(r, interval));
        if (pollAbortRef.current) return;
        let task: SaraTaskView;
        try {
          task = await fetchSaraTask(taskId);
          consecutiveErrors = 0;
        } catch (err) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            fail(dutchSaraError(err, 'Kon de verwerkingsstatus niet ophalen.'), audio);
            return;
          }
          continue;
        }
        // Heropend naar een andere taak terwijl deze fetch liep: niets
        // schrijven, anders landt transcript van taak A in de stand van taak B.
        if (taskIdRef.current !== taskId) return;
        setTaskStatus(task.status);
        if (applyTask(task, audio)) return;
        // pending / transcribing / pseudonymizing / summarizing → blijf pollen
      }
      if (!pollAbortRef.current) {
        fail('De verwerking duurt langer dan verwacht. Probeer het later opnieuw.', audio);
      }
    },
    [applyTask, fail],
  );

  const uploadAndProcess = useCallback(
    async (blob: Blob, durationSec: number, force = false) => {
      // Plausibiliteit vóór de upload: een lege of veel te dunne opname moet
      // hier luid stranden. Uploaden zou een taak opleveren die de hele
      // pijplijn doorloopt en eindigt in lege SOEP-regels zonder uitleg.
      const check = classifyRecording(blob.size, durationSec);
      if (check === 'negligible') {
        // Per ongeluk gestart en meteen gestopt. Hier hoort géén fout bij: er is
        // niets misgegaan en er valt niets uit te leggen, dus geen `fail()` maar
        // stil terug naar de beginstand. De buffer moet daarbij wél weg, anders
        // biedt de volgende paginalaad de misklik als te herstellen opname aan.
        await bufferClear().catch(() => {});
        lastBlobRef.current = null;
        setErrorMsg(null);
        setSeconds(0);
        setPhase('idle');
        return;
      }
      if (check === 'empty') {
        // Alleen hier mag de crash-buffer weg: 'empty' is nul bytes of een
        // opname die lang genoeg duurde om zeker te weten dat er niets in zit
        // (zie classifyRecording). Opnieuw verwerken heeft geen zin (deze
        // controle faalt weer) en zonder wissen biedt het herstelpad bij de
        // volgende paginalaad exact dezelfde lege opname opnieuw aan.
        await bufferClear().catch(() => {});
        lastBlobRef.current = null;
        fail(emptyRecordingMessage(blob.size, durationSec), 'discarded');
        return;
      }
      if (check === 'thin' && !force) {
        // 'thin' is een schatting: er zit wél audio in, alleen minder dan bij
        // deze duur verwacht. De buffer blijft dus staan en lastBlobRef gevuld,
        // zodat "Toch uploaden en verwerken" werkelijk kan slagen. De POH weet
        // zelf of het consult echt zo lang duurde.
        lastBlobRef.current = blob;
        lastDurationRef.current = durationSec;
        fail(thinRecordingMessage(blob.size, durationSec), 'thin');
        return;
      }
      lastBlobRef.current = blob;
      lastDurationRef.current = durationSec;
      setErrorMsg(null);
      setUploadPct(0);
      setPhase('uploading');

      // Het formaat reist met de blob mee: bij een verse opname is het type in
      // `onstop` gezet, bij een herstelde opname komt het uit de buffer-meta.
      // Zo krijgt de signed URL het basistype van wat er werkelijk in de bytes
      // zit, ook bij een opname die vóór deze wijziging gebufferd is (die had
      // geen ander type dan audio/webm).
      const uploadMime = baseMimeType(blob.type);

      let mint: { taskId: string; uploadUrl: string; uploadHeaders?: Record<string, string> };
      try {
        mint = await requestUploadUrl(uploadMime, durationSec);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // Server-flag uit — nette melding, maar de crash-buffer blijft
          // staan: dit kan de enige kopie van een zojuist opgenomen consult
          // zijn (flag uitgezet ná paginalaad). De herstel-prompt biedt de
          // upload opnieuw aan zodra Sara weer beschikbaar is.
          setPhase('unavailable');
          return;
        }
        fail(dutchSaraError(err, 'De upload kon niet worden gestart.'), 'retryable');
        return;
      }

      try {
        await resumableUpload(
          mint.uploadUrl,
          blob,
          (loaded, total) => {
            setUploadPct(total > 0 ? Math.round((loaded / total) * 100) : 0);
          },
          // De headers gaan ongewijzigd door naar GCS: alleen wat de backend
          // heeft meegetekend hoort in de sessie-aanvraag te staan, inclusief de
          // Content-Type. Zelf iets toevoegen breekt de handtekening.
          mint.uploadHeaders ?? {},
        );
        await finalizeRecording(mint.taskId);
        // Upload bevestigd: de crash-buffer mag weg.
        await bufferClear().catch(() => {});
      } catch (err) {
        fail(dutchSaraError(err, 'De upload is mislukt.'), 'retryable');
        return;
      }

      taskIdRef.current = mint.taskId;
      setTaskStatus('pending');
      setPhase('processing');
      // 'retryable': de blob staat nog in lastBlobRef, dus als de serverside
      // verwerking faalt mag het foutpaneel opnieuw uploaden aanbieden.
      void pollTask(mint.taskId, 'retryable');
    },
    [fail, pollTask],
  );

  // Opwarmen tijdens de opname: de GPU-instance van de transcriber heeft
  // 1-2 min koude start + modelinit; door bij opnamestart (en elke 10 min,
  // want Cloud Run schaalt een idle instance na ~15 min weer weg) te pingen
  // valt die wachttijd volledig binnen de duur van het gesprek.
  useEffect(() => {
    if (phase !== 'recording') return;
    void warmupTranscriber().catch(() => {});
    const timer = window.setInterval(
      () => void warmupTranscriber().catch(() => {}),
      10 * 60 * 1000,
    );
    return () => window.clearInterval(timer);
  }, [phase]);

  const startRecording = useCallback(async () => {
    setMicError(null);
    setErrorMsg(null);
    // Wake Lock vóór de eerste await aanvragen, niet pas na het opstarten van
    // de recorder: browsers geven hem alleen af binnen de gebruikershandeling
    // die de aanvraag veroorzaakte, en die vervalt zodra we op getUserMedia
    // wachten. Op mobiel is dit het verschil tussen een scherm dat aanblijft en
    // een scherm dat na dertig seconden vergrendelt.
    void acquireWakeLock();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      releaseWakeLock();
      setMicError(
        'Geen toegang tot de microfoon. Sta microfoontoegang toe in de browser en probeer het opnieuw.',
      );
      return;
    }
    streamRef.current = stream;
    const recordMime = pickRecordMime();
    if (!recordMime) {
      releaseWakeLock();
      stopStream();
      setMicError(UNSUPPORTED_BROWSER_MSG);
      return;
    }
    // Het basistype zonder codec-parameters. Dit label reist mee naar de
    // crash-buffer, de blob, de signed URL en de resumable sessie, zodat wat er
    // in de bucket ligt hetzelfde formaat is als wat de handtekening en het
    // taak-doc beweren.
    const uploadMime = baseMimeType(recordMime);
    chunksRef.current = [];
    startedAtRef.current = Date.now();
    resetGaps();
    await bufferInit(uploadMime).catch(() => {});

    let recorder: MediaRecorder;
    try {
      // 48 kbit/s: ruim genoeg voor spraak (Whisper rekent toch op 16 kHz), en
      // een kwart van de standaard. Scheelt uploadtijd op mobiel internet;
      // de 'thin'-grens in classifyRecording (8 kbit/s) houdt 6x marge.
      recorder = new MediaRecorder(stream, { mimeType: recordMime, audioBitsPerSecond: 48_000 });
    } catch {
      // isTypeSupported kan liegen (oudere Safari); zonder deze catch blijft
      // de microfoonstream open zonder enige melding.
      releaseWakeLock();
      stopStream();
      setMicError(UNSUPPORTED_BROWSER_MSG);
      return;
    }
    recorder.ondataavailable = (ev) => {
      if (ev.data.size === 0) return;
      const now = Date.now();
      noteAudioGap(now);
      lastChunkAtRef.current = now;
      chunksRef.current.push(ev.data);
      const elapsed = Math.floor((now - startedAtRef.current) / 1000);
      void bufferAppend(ev.data, uploadMime, elapsed).catch(() => {});
    };
    recorder.onstop = () => {
      const now = Date.now();
      // Ook hier meten. Stopte de opname op iOS bij het wisselen naar een andere
      // app, dan komt er daarna geen chunk meer binnen en is dit het enige
      // moment waarop het ontbrekende staartstuk nog te zien is.
      noteAudioGap(now);
      // Maar: een browser die `start(timeslice)` negeert levert helemaal geen
      // tussentijdse chunks en pas bij stop() één blob. De gemeten tussenpoos is
      // dan de volledige opnameduur, dus een geslaagd consult van 45 minuten zou
      // zich melden als 45 minuten ontbrekende audio. Safari staat erom bekend en
      // is precies het platform waarvoor dit pad bestaat. Eén chunk is daarom
      // geen bewijs van een onderbreking en de meting vervalt; vanaf twee chunks
      // levert de browser aantoonbaar op tijd en doet de detectie weer waarvoor
      // ze bedoeld is. Wat we hier opgeven is de onderbreking die al vóór de
      // eerste chunk begon en tot het einde duurde; die blijft zichtbaar via de
      // bytecontrole in `classifyRecording`.
      if (chunksRef.current.length <= 1) resetGaps();
      const durationSec = Math.floor((now - startedAtRef.current) / 1000);
      const blob = new Blob(chunksRef.current, { type: uploadMime });
      stopStream();
      // Ook een blob van nul bytes gaat via uploadAndProcess: die legt uit wat
      // er mis is. Eerder viel dit geval terug naar 'idle' en leek het alsof
      // er niets was gebeurd.
      void uploadAndProcess(blob, durationSec);
    };
    recorderRef.current = recorder;
    lastChunkAtRef.current = Date.now();
    setStalled(false);
    recorder.start(TIMESLICE_MS); // chunk naar IndexedDB per timeslice
    setSeconds(0);
    setBackgrounded(false);
    setPhase('recording');
  }, [acquireWakeLock, noteAudioGap, releaseWakeLock, resetGaps, stopStream, uploadAndProcess]);

  const stopRecording = useCallback(() => {
    releaseWakeLock();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop(); // triggert onstop → upload
    }
  }, [releaseWakeLock]);

  // Wat de herstel-prompt vooraf over deze buffer moet zeggen. "Herstellen en
  // uploaden" zet hem daarna zonder verdere waarschuwing door, dus de POH moet
  // hier al weten dat er minder audio in zit dan bij deze duur verwacht.
  const recoveryWarning =
    recovery && classifyRecording(recovery.blob.size, recovery.meta.durationSec) === 'thin'
      ? thinRecordingMessage(recovery.blob.size, recovery.meta.durationSec)
      : null;

  const recoverBuffered = useCallback(() => {
    if (!recovery) return;
    const buf = recovery;
    setRecovery(null);
    // force: het herstelpaneel heeft de te-dun-waarschuwing hierboven al
    // getoond, dus dezelfde controle hoeft de POH niet nog eens tegen te
    // houden. Zonder force belandt een bewaarde opname in het foutpaneel en
    // biedt de volgende paginalaad hem opnieuw aan: precies de lus die het
    // wissen ooit moest voorkomen. Een zeker lege buffer bereikt dit pad niet;
    // die is bij het laden al opgeruimd.
    void uploadAndProcess(buf.blob, buf.meta.durationSec, true);
  }, [recovery, uploadAndProcess]);

  const discardBuffered = useCallback(async () => {
    setRecovery(null);
    await bufferClear().catch(() => {});
    setPhase('idle');
  }, []);

  const retryUpload = useCallback(() => {
    if (!lastBlobRef.current) return;
    // force, want deze knop verschijnt alleen als dezelfde blob de controle al
    // gepasseerd is (goedgekeurd of bewust doorgezet). Zonder force zou een
    // netwerkfout halverwege de upload van een doorgezette opname de POH terug
    // naar de te-dun-waarschuwing sturen. De zekerheidscontrole op een lege
    // opname staat vóór de force-poort en blijft dus gelden.
    void uploadAndProcess(lastBlobRef.current, lastDurationRef.current, true);
  }, [uploadAndProcess]);

  // Ontsnappingsroute uit de te-dun-melding: verwerk deze opname toch. Zonder
  // force zou dezelfde controle opnieuw afkeuren.
  const uploadThinAnyway = useCallback(() => {
    if (!lastBlobRef.current) return;
    void uploadAndProcess(lastBlobRef.current, lastDurationRef.current, true);
  }, [uploadAndProcess]);

  // Wis een opname vroegtijdig (vóór de 72u-opruiming). Bij een fout blijft de
  // regel gewoon staan zodat de POH het opnieuw kan proberen.
  const removeTask = useCallback(async (taskId: string) => {
    setConfirmDeleteId(null);
    try {
      await deleteSaraTask(taskId);
      setRecentTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (err) {
      // 404 = serverside al weg (janitor of ander apparaat): regel alsnog weg.
      // Andere fouten: regel blijft staan; opnieuw proberen kan direct.
      if (err instanceof ApiError && err.status === 404) {
        setRecentTasks((prev) => prev.filter((t) => t.id !== taskId));
      }
    }
  }, []);

  // Heropen een taak uit de lijst met recente opnames: eenmalig ophalen en de
  // status toepassen; loopt de pijplijn nog, dan pollen zoals na een upload.
  const openRecentTask = useCallback(
    async (taskId: string) => {
      // Kill een eventuele lopende poll-loop van een eerder geopende taak.
      pollAbortRef.current = true;
      taskIdRef.current = taskId;
      setErrorMsg(null);
      let task: SaraTaskView;
      try {
        task = await fetchSaraTask(taskId);
      } catch (err) {
        fail(dutchSaraError(err, 'Kon de opname niet openen.'));
        return;
      }
      setTaskStatus(task.status);
      // 'none': lastBlobRef hoort bij een eerdere eigen opname, niet bij deze
      // taak. Opnieuw uploaden zou dus de vórige opname als nieuwe taak
      // insturen; de audio van déze taak staat serverside.
      if (applyTask(task, 'none')) return;
      setPhase('processing');
      void pollTask(taskId, 'none');
    },
    [applyTask, fail, pollTask],
  );

  const reset = useCallback(() => {
    pollAbortRef.current = true;
    testAbortRef.current = true;
    releaseWakeLock();
    stopStream();
    lastBlobRef.current = null;
    taskIdRef.current = null;
    setFailedAudio('none');
    setPhase('idle');
    setSeconds(0);
    setTranscript('');
    setSoep(EMPTY_SOEP);
    setSummary('');
    setKennis('');
    setMetrics(null);
    setUploadPct(0);
    setTaskStatus(null);
    setErrorMsg(null);
    setMicError(null);
    setBackgrounded(false);
    resetGaps();
    setTestStep(0);
    setHisConfirm(false);
    setHisNotice(false);
    setBgNotice(false);
    void bufferClear().catch(() => {});
  }, [releaseWakeLock, resetGaps, stopStream]);

  // Naar de achtergrond: de servertaak loopt gewoon door (de pijplijn draait
  // volledig serverside); we stoppen alleen het lokale pollen en maken de
  // pagina vrij voor een volgende opname. De taak is terug te vinden onder
  // "Recente opnames" (openRecentTask pakt het pollen daar weer op). Alleen
  // vanuit 'processing' — tijdens 'uploading' loopt de upload nog in deze tab
  // en zou weggaan de opname afbreken.
  const continueInBackground = useCallback(() => {
    reset();
    setBgNotice(true);
  }, [reset]);

  // "Verwerkt in HIS": de POH heeft de SOEP-velden in het HIS overgenomen; de
  // opname (doc, transcript, SOEP en eventuele audio) wordt direct definitief
  // verwijderd — dataminimalisatie, er is geen reden meer om te bewaren.
  const markProcessedInHis = useCallback(async () => {
    const taskId = taskIdRef.current;
    if (!taskId) return;
    setHisBusy(true);
    try {
      await deleteSaraTask(taskId);
    } catch (err) {
      // 404 = serverside al weg (janitor of ander apparaat): zelfde eindstand.
      if (!(err instanceof ApiError && err.status === 404)) {
        setHisBusy(false);
        setHisConfirm(false);
        setErrorMsg(dutchSaraError(err, 'Verwijderen is niet gelukt. Probeer het opnieuw.'));
        return;
      }
    }
    setHisBusy(false);
    reset();
    setHisNotice(true);
  }, [reset]);

  // Demo/test-variant van "Verwerkt in HIS": geen servertaak om te wissen,
  // wel dezelfde eindstand (terug naar idle + melding) zodat de demo de hele
  // afronding toont.
  const demoProcessedInHis = useCallback(() => {
    reset();
    setHisNotice(true);
  }, [reset]);

  // Test-/demo-opname: geen microfoon, geen upload — alleen een timer die de
  // opname-UX nabootst zodat de POH het pad "opnemen → stop → verwerken →
  // review" kan doorlopen zonder de echte pijplijn. Elke start pakt het
  // volgende voorbeeldconsult, zodat herhaalde demo's variatie tonen.
  const demoIdxRef = useRef(-1);
  const startTestRecording = useCallback(() => {
    demoIdxRef.current = (demoIdxRef.current + 1) % DEMO_CONSULTEN.length;
    setErrorMsg(null);
    setMicError(null);
    setTestStep(0);
    setTranscript('');
    setSoep(EMPTY_SOEP);
    setSummary('');
    setKennis('');
    setMetrics(null);
    startedAtRef.current = Date.now();
    setSeconds(0);
    setPhase('test-recording');
  }, []);

  // Speelt de drie pijplijnstappen getimed af en vult daarna in één keer
  // transcript, samenvatting én SOEP in (in de gekozen stijl) — precies zoals
  // de echte pijplijn dat doet. De eerdere "Zet om in SOEP-verslag"-tussenstap
  // bestond alleen in de demo en is daarom verwijderd (besluit 20-8-2026).
  const runTestPipeline = useCallback(async () => {
    testAbortRef.current = false;
    setPhase('test-processing');
    for (let i = 0; i < PIPELINE_STEPS.length; i += 1) {
      setTestStep(i);
      await new Promise((r) => setTimeout(r, TEST_STEP_MS));
      if (testAbortRef.current) return;
    }
    const consult = DEMO_CONSULTEN[Math.max(0, demoIdxRef.current)];
    setTranscript(consult.transcript);
    setSummary(consult.summary);
    setSoep(consult.soep[soepStyle]);
    setPhase('test-review');
  }, [soepStyle]);

  const stopTestRecording = useCallback(() => {
    void runTestPipeline();
  }, [runTestPipeline]);

  // Demo (issue #43): de voorbeeldopname loopt vanzelf af — wie de stopknop
  // niet vindt, wacht anders eeuwig. Eerder stoppen mag nog steeds.
  useEffect(() => {
    if (!demo || phase !== 'test-recording') return;
    const t = setTimeout(() => void runTestPipeline(), 6000);
    return () => clearTimeout(t);
  }, [demo, phase, runTestPipeline]);

  // Verslag opnieuw genereren (echte pijplijn): server zet de status op
  // 'summarizing' en de agent maakt op het bewaarde transcript een nieuw SOEP
  // in de gekozen stijl; de bestaande poll-flow brengt ons terug in de review.
  const regenerateSoep = useCallback(async () => {
    const taskId = taskIdRef.current;
    if (!taskId) return;
    setErrorMsg(null);
    try {
      await regenerateSaraTask(taskId, soepStyle);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError && err.status === 409
          ? 'Opnieuw genereren kan niet meer: het transcript is niet meer beschikbaar of het verslag is al verwerkt.'
          : 'Opnieuw genereren is niet gelukt. Probeer het over een moment nog eens.',
      );
      return;
    }
    setPhase('processing');
    void pollTask(taskId, 'none');
  }, [soepStyle, pollTask]);

  // Demo-variant: wissel client-side naar de voorgegenereerde stijlvariant van
  // hetzelfde consult — geen servercall, wel hetzelfde resultaatgedrag.
  const regenerateTestSoep = useCallback(() => {
    setSoep(DEMO_CONSULTEN[Math.max(0, demoIdxRef.current)].soep[soepStyle]);
  }, [soepStyle]);

  // Overdracht naar Mila: gepseudonimiseerde SOEP + intentie gaan mee (ADR
  // 0006) en Mila verstuurt de vraag direct (auto-send, besluit Jos 2026-07-23).
  const openMila = useCallback(
    (mode: MilaMode, onderwerp = '') => {
      navigate('/kennisagent', {
        state: {
          saraPlan: buildMilaQuestion(mode, soep, summary, onderwerp),
          saraMode: mode,
          // Structured SOEP + summary + taskId mee, zodat "Terug naar Sara" de
          // review-stand compleet herstelt (Mila's antwoord komt naast het
          // verslag, zie KennisCard). Blijft in de router-state, gaat niet naar
          // de agent.
          saraSoep: soep,
          saraSummary: summary,
          saraTaskId: taskIdRef.current,
        },
      });
    },
    [navigate, soep, summary],
  );

  // Concept-verwijsbrief uit het transcript (niet de SOEP); de brief leeft 72 uur onder
  // Ondersteuning > Verwijsbrieven (ADR 0017).
  const maakBrief = useCallback(async () => {
    // Inhoud uit het transcript, niet uit de SOEP (besluit Jos 12-10-2026).
    const b = await maakVerwijsbriefUitSara(
      { bron: 'sara', saraTaskId: taskIdRef.current },
      user?.displayName ?? '',
    );
    navigate(`/verwijsbrieven/${b.id}`);
  }, [navigate, user?.displayName]);
  const onVerwijsbrief = user?.verwijsbrief ? maakBrief : undefined;

  const completed = phase === 'review' || phase === 'saved';
  const isRecording = phase === 'recording' || phase === 'test-recording';

  const headerIdx = stepIndex(phase);

  return (
    <Layout>
      <style>{`
        @keyframes sarabar {
          from { transform: scaleY(0.35); opacity: 0.6; }
          to   { transform: scaleY(1);    opacity: 1; }
        }
      `}</style>

      <div className="flex h-full flex-col">
        {/* Page header */}
        <div className="border-b border-brand-100 bg-white px-4 py-3 sm:px-6">
          <StepBar idx={headerIdx} />
        </div>

        {/* Body — stacks on mobile, side-by-side on lg+ */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-28 lg:flex-row">
          {/* Left panel — recording controls */}
          <div className="flex w-full shrink-0 flex-col gap-4 lg:w-72">
            <div className="flex flex-col items-center gap-4 rounded-lg border border-brand-100 bg-white px-6 py-8">
              {(phase === 'idle' || phase === 'recovery' || phase === 'unavailable') && demo && (
                // Demo (issue #43): de voorbeeldopname is de enige workflow —
                // geen microfoonknop, dus de browser vraagt nooit om toegang.
                <div className="flex w-full flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={startTestRecording}
                    aria-label="Voorbeeldopname afspelen"
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500 shadow-md transition-all hover:bg-brand-600 hover:shadow-lg active:scale-95"
                  >
                    <PlayIcon size={32} className="text-white" strokeWidth={1.75} />
                  </button>
                  <p className="text-center text-[12px] text-ink-900/50">
                    Voorbeeldopname afspelen: een gesimuleerd consult, geen microfoon nodig
                  </p>
                </div>
              )}

              {(phase === 'idle' || phase === 'recovery' || phase === 'unavailable') && !demo && (
                <>
                  {/* De gesimuleerde demo-opname bestaat alleen nog in de
                      demo-omgeving (besluit Jos 21-9-2026), dus hier is de
                      echte opname de enige workflow en hoeft die geen label. */}
                  <div className="flex w-full flex-col items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void startRecording()}
                      disabled={phase !== 'idle'}
                      aria-label="Opname starten"
                      className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500 shadow-md transition-all hover:bg-brand-600 hover:shadow-lg active:scale-95 disabled:opacity-40"
                    >
                      <MicIcon size={32} className="text-white" strokeWidth={1.75} />
                    </button>
                    <p className="text-center text-[12px] text-ink-900/50">
                      Neem het consult op; transcriptie en SOEP volgen automatisch
                    </p>
                    {/* AVG: opnemen mag alleen met toestemming van de cliënt
                        (verzoek Jos 20-8-2026). Voorlichting, geen blokkade —
                        een aanvink-hokje bewijst niets en de POH is zelf
                        verwerkingsverantwoordelijke voor het gesprek. */}
                    <p className="w-full rounded-md border border-brand-100 bg-brand-50/50 px-3 py-2 text-left text-[11px] leading-relaxed text-ink-900/70">
                      Vraag de cliënt vóór de opname om toestemming voor het opnemen van dit gesprek
                      en start de opname alleen na akkoord (AVG).
                    </p>
                    {/* Voorlichting in plaats van blokkade. De grens is technisch
                        en niet weg te programmeren: een webapp kan op iOS niet
                        blijven opnemen als de gebruiker naar een andere app gaat.
                        Daarom vooraf precies zeggen wat wél werkt, en erbij
                        vertellen dat een onderbreking gemeld wordt, zodat de POH
                        weet dat hij niet zelf hoeft te controleren. */}
                    {IS_MOBILE && (
                      <p className="w-full rounded-md border border-mosterdgeel-100 bg-mosterdgeel-10 px-3 py-2 text-left text-[11px] leading-relaxed text-ink-900/70">
                        Op een telefoon of tablet neemt Sara alleen op zolang deze app op de
                        voorgrond staat. Houd het scherm aan en ga tijdens het consult niet naar een
                        andere app. Valt de opname toch stil, dan meldt Sara dat meteen.
                      </p>
                    )}
                    {micError && (
                      <p className="w-full rounded-md border border-error/30 bg-red-50/60 px-3 py-2 text-center text-[12px] text-error">
                        {micError}
                      </p>
                    )}
                  </div>
                  {phase === 'idle' && (
                    <div className="flex w-full flex-col gap-2 border-t border-brand-50 pt-4">
                      <label
                        htmlFor="soep-stijl"
                        className="text-center text-[11px] font-semibold uppercase tracking-wider text-ink-900/50"
                      >
                        SOEP-stijl
                      </label>
                      <select
                        id="soep-stijl"
                        value={soepStyle}
                        onChange={(e) => changeSoepStyle(e.target.value as SaraSoepStyle)}
                        className="w-full rounded-md border border-brand-100 bg-white px-2 py-1.5 text-[12px] text-ink-900"
                      >
                        <option value="steekwoorden">Steekwoorden</option>
                        <option value="bondig">Bondig (standaard)</option>
                        <option value="uitgebreid">Uitgebreid</option>
                      </select>
                      <p className="text-center text-[11px] text-ink-900/40">
                        Bepaalt hoe Sara het SOEP-verslag formuleert, vanaf je volgende opname
                      </p>
                    </div>
                  )}
                </>
              )}

              {isRecording && (
                <>
                  <div className="relative">
                    <span className="absolute inset-0 animate-ping rounded-full bg-error/20" />
                    <button
                      type="button"
                      onClick={phase === 'test-recording' ? stopTestRecording : stopRecording}
                      aria-label="Opname stoppen"
                      className="relative flex h-20 w-20 items-center justify-center rounded-full bg-error shadow-md transition-all hover:bg-red-700 active:scale-95"
                    >
                      <SquareIcon size={22} className="text-white" strokeWidth={2} />
                    </button>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <span className="font-mono text-[22px] font-semibold tabular-nums text-ink-900">
                      {fmt(seconds)}
                    </span>
                    <Waveform />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-error">
                      {phase === 'test-recording' ? 'Demo-opname bezig' : 'Opname bezig'}
                    </span>
                  </div>
                </>
              )}

              {(phase === 'uploading' || phase === 'processing' || phase === 'test-processing') && (
                <>
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50">
                    {phase === 'uploading' ? (
                      <UploadCloudIcon size={30} className="text-brand-600" strokeWidth={1.75} />
                    ) : (
                      <Loader2Icon
                        size={30}
                        className="animate-spin text-brand-600"
                        strokeWidth={1.75}
                      />
                    )}
                  </div>
                  <p className="text-center text-[12px] text-ink-900/50">
                    {phase === 'uploading' ? 'Audio uploaden…' : 'Verwerken…'}
                  </p>
                  {phase === 'processing' && (
                    <div className="flex w-full flex-col items-center gap-2 border-t border-brand-50 pt-4">
                      <button
                        type="button"
                        onClick={continueInBackground}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-brand-100 bg-white px-4 py-2 text-[12px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900 active:scale-95"
                      >
                        <MicIcon size={13} strokeWidth={2} />
                        Verwerk op de achtergrond
                      </button>
                      <p className="text-center text-[11px] text-ink-900/40">
                        De verwerking loopt door; je kunt direct een volgend consult opnemen. Het
                        verslag vind je straks terug onder Recente opnames.
                      </p>
                    </div>
                  )}
                </>
              )}

              {phase === 'test-review' && (
                <>
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50">
                    <CheckIcon size={32} className="text-brand-600" strokeWidth={2} />
                  </div>
                  <p className="text-center text-[12px] text-ink-900/50">Demo-opname verwerkt</p>
                  <button
                    type="button"
                    onClick={reset}
                    className="flex items-center gap-1.5 rounded-md border border-brand-100 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900 active:scale-95"
                  >
                    <RotateCcwIcon size={13} strokeWidth={2} />
                    Nieuwe demo-opname
                  </button>
                </>
              )}

              {(phase === 'review' || phase === 'saved' || phase === 'failed') && (
                <>
                  <div
                    className={`flex h-20 w-20 items-center justify-center rounded-full ${
                      phase === 'failed' ? 'bg-red-50' : 'bg-brand-50'
                    }`}
                  >
                    {phase === 'failed' ? (
                      <AlertTriangleIcon size={30} className="text-error" strokeWidth={2} />
                    ) : (
                      <CheckIcon size={32} className="text-brand-600" strokeWidth={2} />
                    )}
                  </div>
                  <p className="text-center text-[12px] text-ink-900/50">
                    {phase === 'failed'
                      ? 'Verwerking mislukt'
                      : phase === 'saved'
                        ? 'Opgeslagen'
                        : 'Opname verwerkt'}
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    className="flex items-center gap-1.5 rounded-md border border-brand-100 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900 active:scale-95"
                  >
                    <RotateCcwIcon size={13} strokeWidth={2} />
                    Nieuwe opname
                  </button>
                </>
              )}
            </div>

            {/* Onderbreking tijdens de opname, in twee lagen: de acute melding
                (er komt nú geen audio binnen) en daaronder de teller die blijft
                staan zodra er audio gemist is. De tweede is het belangrijkst op
                mobiel, want wie terugkomt uit een andere app ziet de acute
                melding misschien nooit. */}
            {phase === 'recording' && stalled && (
              <div className="flex items-start gap-2 rounded-lg border border-error/40 bg-red-50/70 p-3 text-[12px] leading-relaxed text-ink-900/80">
                <AlertTriangleIcon
                  size={15}
                  className="mt-0.5 shrink-0 text-error"
                  strokeWidth={2}
                />
                <span>
                  Er komt geen audio meer binnen. Houd Sara op de voorgrond en het scherm aan,
                  anders ontbreekt dit deel van het consult.
                </span>
              </div>
            )}

            {phase === 'recording' && gapCount > 0 && !stalled && (
              <div className="flex items-start gap-2 rounded-lg border border-mosterdgeel-100 bg-mosterdgeel-10 p-3 text-[12px] leading-relaxed text-ink-900/80">
                <AlertTriangleIcon
                  size={15}
                  className="mt-0.5 shrink-0 text-warning"
                  strokeWidth={2}
                />
                <span>
                  De opname is onderbroken geweest:{' '}
                  {gapCount === 1 ? 'één keer' : `${gapCount} keer`}, samen ongeveer {fmt(gapSec)}.
                  Dat deel van het consult is niet opgenomen.
                </span>
              </div>
            )}

            {phase === 'recording' && backgrounded && !stalled && (
              <div className="flex items-start gap-2 rounded-lg border border-brand-100 bg-brand-50/60 p-3 text-[12px] leading-relaxed text-ink-900/70">
                <EyeOffIcon size={15} className="mt-0.5 shrink-0 text-brand-600" strokeWidth={2} />
                {/* Op een computer loopt een verborgen tabblad gewoon door; op
                    mobiel is dat juist niet zo, en dan mag hier geen
                    geruststelling staan die niet waargemaakt wordt. */}
                <span>
                  {IS_MOBILE
                    ? 'Sara stond op de achtergrond. Op een telefoon of tablet kan de opname daardoor stilvallen.'
                    : 'De opname loopt door terwijl dit tabblad op de achtergrond staat.'}
                </span>
              </div>
            )}

            {/* Werkinstructie — alleen op idle; demo krijgt de gescripte flow. */}
            {phase === 'idle' && (
              <div className="rounded-lg border border-brand-50 bg-[#f7f4ea] p-4 text-[12px] leading-relaxed text-ink-900/70">
                <p className="font-semibold text-ink-900/80">
                  {demo ? 'Zo werkt Sara' : 'Werkinstructie'}
                </p>
                <ol className="mt-2 list-decimal space-y-1.5 pl-4">
                  {demo ? (
                    <>
                      <li>Speel de voorbeeldopname af; het transcript verschijnt vanzelf.</li>
                      <li>Bekijk en bewerk het transcript en de samenvatting.</li>
                      <li>Zet om naar een SOEP-verslag en kopieer per veld naar het HIS.</li>
                    </>
                  ) : (
                    <>
                      <li>Tik op de microfoon en begin met dicteren of het consult te voeren.</li>
                      <li>Spreek duidelijk en in volzinnen.</li>
                      <li>Stop de opname; de audio wordt geüpload en verwerkt.</li>
                      <li>Controleer het transcript en de SOEP-velden.</li>
                      <li>Bevestig om het SOEP-concept vast te leggen.</li>
                    </>
                  )}
                </ol>
                {demo && (
                  <p className="mt-3 border-t border-brand-100 pt-2 text-[11px] text-ink-900/50">
                    In de echte omgeving neemt Sara het consult via de microfoon op en verwerkt een
                    beveiligde pijplijn de audio automatisch.
                  </p>
                )}
              </div>
            )}

            {/* Patiëntenfolder — de POH toont of print deze bij het vragen om
                toestemming voor de opname. Statisch bestand uit public/. */}
            {phase === 'idle' && (
              <a
                href="/privacy-statement-patienten.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 rounded-lg border border-brand-100 bg-white p-4 text-[12px] leading-relaxed text-ink-900/70 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              >
                <FileTextIcon
                  size={15}
                  className="mt-0.5 shrink-0 text-brand-600"
                  strokeWidth={2}
                />
                <span>
                  <span className="font-semibold text-ink-900/80">
                    Patiëntenfolder &quot;Aandacht voor uw verhaal&quot;
                  </span>
                  <span className="mt-0.5 block text-ink-900/50">
                    Toon of print deze folder als u toestemming vraagt voor de opname.
                  </span>
                </span>
              </a>
            )}
          </div>

          {/* Right panel — content */}
          <div className="flex flex-1 flex-col gap-4">
            {/* Gemeten onderbrekingen blijven na de opname staan, door alle
                vervolgstappen heen tot aan de review. Dit is het enige signaal
                dat niet van de omvang van de blob afhangt: een opname die
                halverwege stilviel kan qua bytes prima plausibel blijven (zeker
                in AAC, dat een hogere bitrate heeft dan Opus) en dan gewoon een
                SOEP opleveren waarin het halve consult ontbreekt. */}
            {gapSec > 0 && phase !== 'recording' && phase !== 'idle' && (
              <div className="flex items-start gap-2 rounded-lg border border-mosterdgeel-100 bg-mosterdgeel-10 p-3 text-[12px] leading-relaxed text-ink-900/80">
                <AlertTriangleIcon
                  size={15}
                  className="mt-0.5 shrink-0 text-warning"
                  strokeWidth={2}
                />
                <span>{gapMessage(gapCount, gapSec)}</span>
              </div>
            )}

            {/* Bevestiging na "Verwerkt in HIS" — opname is definitief weg. */}
            {phase === 'idle' && hisNotice && (
              <div className="flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50/60 p-3 text-[12px] leading-relaxed text-ink-900/80">
                <CheckIcon size={15} className="mt-0.5 shrink-0 text-brand-600" strokeWidth={2.5} />
                <span>
                  Verwerkt in het HIS. De opname, het transcript en het SOEP-verslag zijn
                  verwijderd.
                </span>
              </div>
            )}

            {/* Melding na "Verwerk op de achtergrond" — taak loopt door. */}
            {phase === 'idle' && bgNotice && (
              <div className="flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50/60 p-3 text-[12px] leading-relaxed text-ink-900/80">
                <Loader2Icon
                  size={15}
                  className="mt-0.5 shrink-0 animate-spin text-brand-600"
                  strokeWidth={2.5}
                />
                <span>
                  De opname wordt op de achtergrond verwerkt. Zodra het verslag klaar is, vind je
                  het hieronder bij Recente opnames.
                </span>
              </div>
            )}

            {/* Idle — recente opnames (72u-retentie) om terug te vinden na een
                refresh; zonder taken de lege staat. */}
            {phase === 'idle' && recentTasks.length > 0 && (
              <div className="rounded-lg border border-brand-100 bg-white">
                <div className="border-b border-brand-50 px-4 py-3 sm:px-5">
                  <h2 className="text-[13px] font-semibold text-ink-900">Recente opnames</h2>
                  <p className="mt-0.5 text-[11px] text-ink-900/50">
                    Tot 72 uur na de opname beschikbaar; daarna worden transcript en SOEP
                    automatisch gewist.
                  </p>
                </div>
                <ul className="divide-y divide-brand-50">
                  {recentTasks.map((t) => (
                    <li key={t.id} className="flex items-center gap-1 pr-2 sm:pr-3">
                      <button
                        type="button"
                        onClick={() => void openRecentTask(t.id)}
                        className="flex flex-1 items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-brand-50/50 sm:px-5"
                      >
                        <div>
                          <p className="text-[13px] font-medium text-ink-900">
                            {fmtDateTime(t.createdAt)}
                          </p>
                          {t.durationSec !== undefined && (
                            <p className="mt-0.5 text-[11px] tabular-nums text-ink-900/50">
                              {fmt(t.durationSec)}
                            </p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                            t.status === 'failed'
                              ? 'bg-red-50 text-error'
                              : t.status === 'pending_approval'
                                ? 'bg-brand-100 text-brand-800'
                                : 'bg-brand-50 text-ink-900/60'
                          }`}
                        >
                          {STATUS_LABEL[t.status]}
                        </span>
                      </button>
                      {confirmDeleteId === t.id ? (
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => void removeTask(t.id)}
                            className="rounded-md bg-error px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-red-700"
                          >
                            Wissen
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="rounded-md border border-brand-100 px-2 py-1.5 text-[11px] text-ink-900/60 transition-colors hover:border-brand-300 hover:text-ink-900"
                          >
                            Annuleer
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(t.id)}
                          aria-label="Opname verwijderen"
                          title="Verwijder deze opname nu (transcript, SOEP en audio)"
                          className="shrink-0 rounded-md p-2 text-ink-900/40 transition-colors hover:bg-red-50 hover:text-error"
                        >
                          <Trash2Icon size={15} strokeWidth={2} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Idle — lege staat */}
            {phase === 'idle' && recentTasks.length === 0 && (
              <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-brand-200 bg-white p-8">
                <div className="flex max-w-xs flex-col items-center gap-3 text-center">
                  <MicIcon size={28} className="text-brand-300" strokeWidth={1.5} />
                  <p className="text-[13px] text-ink-900/40">
                    Het transcript en de SOEP-velden verschijnen hier na de opname en verwerking.
                  </p>
                </div>
              </div>
            )}

            {/* Herstel van achtergebleven opname */}
            {phase === 'recovery' && recovery && (
              <div className="rounded-lg border border-brand-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <UploadCloudIcon
                    size={22}
                    className="mt-0.5 shrink-0 text-brand-600"
                    strokeWidth={1.75}
                  />
                  <div className="flex-1">
                    <h2 className="text-[14px] font-semibold text-ink-900">
                      Niet-afgeronde opname gevonden
                    </h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-ink-900/60">
                      Er staat nog een opname van {fmt(recovery.meta.durationSec)} op dit apparaat
                      die niet is geüpload. Wil je deze herstellen en alsnog uploaden?
                    </p>
                    {recoveryWarning && (
                      <p className="mt-2 text-[12px] leading-relaxed text-ink-900/50">
                        {recoveryWarning}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={recoverBuffered}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 active:scale-95"
                      >
                        Herstellen en uploaden
                      </button>
                      <button
                        type="button"
                        onClick={() => void discardBuffered()}
                        className="rounded-lg border border-brand-100 bg-white px-4 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900"
                      >
                        Verwijderen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Server-flag uit */}
            {phase === 'unavailable' && (
              <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-brand-200 bg-white p-8">
                <div className="flex max-w-sm flex-col items-center gap-3 text-center">
                  <AlertTriangleIcon size={26} className="text-brand-400" strokeWidth={1.75} />
                  <p className="text-[14px] font-semibold text-ink-900">
                    Sara-transcriptie is nog niet beschikbaar
                  </p>
                  <p className="text-[13px] leading-relaxed text-ink-900/50">
                    De transcriptiepijplijn staat op dit moment uit. Je opname is lokaal bewaard;
                    zodra Sara weer beschikbaar is, krijg je bij het openen van deze pagina de
                    mogelijkheid om de opname alsnog te verwerken. Neem contact op met SUPPOHRT als
                    je verwacht dat deze functie voor jou beschikbaar zou moeten zijn.
                  </p>
                </div>
              </div>
            )}

            {/* Opname bezig (echt of demo) */}
            {isRecording && (
              <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-error/30 bg-red-50/40 p-8">
                <p className="text-[13px] text-ink-900/50">
                  {phase === 'test-recording'
                    ? 'Demo-opname bezig — klik op stop wanneer je klaar bent.'
                    : 'Opname bezig — spreek duidelijk.'}
                </p>
              </div>
            )}

            {/* Upload-voortgang */}
            {phase === 'uploading' && (
              <div className="rounded-lg border border-brand-100 bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[13px] font-semibold text-ink-900">Audio uploaden</h2>
                  <span className="text-[12px] tabular-nums text-ink-900/50">{uploadPct}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-brand-50">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-all"
                    style={{ width: `${uploadPct}%` }}
                  />
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-ink-900/50">
                  Sluit dit tabblad niet. De upload gaat verder na een korte netwerkonderbreking.
                </p>
              </div>
            )}

            {/* Pijplijn-voortgang — echte pijplijn (uit taskStatus) */}
            {phase === 'processing' && (
              <div className="rounded-lg border border-brand-100 bg-white p-5">
                <h2 className="mb-4 text-[13px] font-semibold text-ink-900">
                  Verwerken: {taskStatus ? STATUS_LABEL[taskStatus] : 'In wachtrij'}
                </h2>
                <PipelineProgress current={taskStatus ? STATUS_ORDER.indexOf(taskStatus) - 1 : 0} />
              </div>
            )}

            {/* Pijplijn-voortgang — test-pijplijn (uit stap-index) */}
            {phase === 'test-processing' && (
              <div className="rounded-lg border border-brand-100 bg-white p-5">
                <h2 className="mb-4 text-[13px] font-semibold text-ink-900">
                  Verwerken: {PIPELINE_STEPS[testStep]?.label ?? 'In wachtrij'}
                </h2>
                <PipelineProgress current={testStep} />
              </div>
            )}

            {/* Mislukt */}
            {phase === 'failed' && (
              <div className="rounded-lg border border-error/30 bg-red-50/40 p-5">
                <div className="flex items-start gap-3">
                  <AlertTriangleIcon
                    size={20}
                    className="mt-0.5 shrink-0 text-error"
                    strokeWidth={2}
                  />
                  <div className="flex-1">
                    {/* Bij 'thin' is er niets mislukt: de opname is bewaard en
                        er wordt om een bevestiging gevraagd. Een korte, bewuste
                        opname komt hier ook terecht, en die met "Er is iets
                        misgegaan" begroeten stuurt de POH het verkeerde bos in. */}
                    <h2 className="text-[14px] font-semibold text-ink-900">
                      {failedAudio === 'thin' ? 'Controleer deze opname' : 'Er is iets misgegaan'}
                    </h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-ink-900/60">
                      {errorMsg ?? 'De verwerking is mislukt.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {failedAudio === 'retryable' && (
                        <button
                          type="button"
                          onClick={retryUpload}
                          className="rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 active:scale-95"
                        >
                          Opnieuw uploaden
                        </button>
                      )}
                      {failedAudio === 'thin' && (
                        <button
                          type="button"
                          onClick={uploadThinAnyway}
                          className="rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 active:scale-95"
                        >
                          Toch uploaden en verwerken
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={reset}
                        className="rounded-lg border border-brand-100 bg-white px-4 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:border-brand-300 hover:text-ink-900"
                      >
                        Opnieuw opnemen
                      </button>
                    </div>
                    <p className="mt-3 text-[11px] text-ink-900/40">
                      {failedAudio === 'discarded'
                        ? 'Er is geen audio geüpload en de opname is niet bewaard.'
                        : failedAudio === 'thin'
                          ? 'De opname staat nog op dit apparaat en gaat verloren zodra je een nieuwe opname start of hierboven op Opnieuw opnemen klikt.'
                          : 'De audio blijft nog maximaal 24 uur beschikbaar om opnieuw te verwerken.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Test-review: zelfde opbouw als de echte review (transcript →
                samenvatting → SOEP), alleen met een voorbeeldconsult-banner.
                De eerdere "Zet om in SOEP-verslag"-tussenstap bestond niet in
                de echte pijplijn en is verwijderd (besluit 20-8-2026). */}
            {phase === 'test-review' && (
              <>
                <div className="rounded-lg border border-petrol-100 bg-petrol-50/40 px-3 py-2 text-[11px] text-petrol-700">
                  Voorbeeldconsult (fictief). Er is geen microfoon gebruikt en er gaat geen data
                  naar buiten.
                </div>

                <div className="rounded-lg border border-brand-100 bg-white p-4 sm:p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-[13px] font-semibold text-ink-900">Transcript</h2>
                    <span className="text-[11px] text-ink-900/50">Gepseudonimiseerd</span>
                  </div>
                  {/* Auto-grow (feedback Aysa 19-8): field-sizing tot 45vh,
                      daarna intern scrollen; resize-y als Safari-fallback. */}
                  <textarea
                    value={transcript}
                    readOnly
                    rows={5}
                    className="max-h-[45vh] w-full resize-y rounded-md border border-brand-100 bg-[#f7fafa] px-3 py-2.5 text-[13px] leading-relaxed text-ink-900/80 [field-sizing:content] focus:outline-none"
                  />
                </div>

                <SummaryCard value={summary} onChange={setSummary} />

                <div className="rounded-lg border border-brand-100 bg-white">
                  <div className="border-b border-brand-50 px-4 py-4 sm:px-5">
                    <h2 className="text-[13px] font-semibold text-ink-900">SOEP-verslag</h2>
                    <p className="mt-0.5 text-[11px] text-ink-900/50">
                      Controleer, bewerk waar nodig en kopieer per veld naar het HIS.
                    </p>
                  </div>

                  <div className="divide-y divide-brand-50">
                    {(Object.keys(soep) as (keyof Soep)[]).map((key) => (
                      <div
                        key={key}
                        className={`px-4 py-4 sm:px-5 ${key === 'P' ? 'bg-brand-50/50' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-900/50">
                            {SOEP_LABELS[key]}
                          </label>
                          <CopyFieldButton text={soep[key]} />
                        </div>
                        <textarea
                          value={soep[key]}
                          onChange={(e) => setSoep((prev) => ({ ...prev, [key]: e.target.value }))}
                          rows={3}
                          className="mt-2 max-h-[45vh] w-full resize-y rounded-md border border-brand-100 bg-white px-3 py-2 text-[13px] leading-relaxed text-ink-900 [field-sizing:content] focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                        {key === 'P' && (
                          <MilaHandoff
                            onOpen={openMila}
                            onVerwijsbrief={onVerwijsbrief}
                            defaultOnderwerp={eersteRegel(soep.E)}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <RegenerateRow
                    soepStyle={soepStyle}
                    onStyleChange={changeSoepStyle}
                    onRegenerate={regenerateTestSoep}
                  />

                  {/* Afronden zoals echt: "Verwerkt in HIS" met dezelfde
                      twee-staps bevestiging. Client-side (er is geen
                      servertaak): terug naar idle met dezelfde melding.
                      Verving de demo-knop "Verslag opslaan" (verzoek Jos
                      20-8-2026). */}
                  <HisRow
                    confirming={hisConfirm}
                    busy={false}
                    onStart={() => setHisConfirm(true)}
                    onCancel={() => setHisConfirm(false)}
                    onConfirm={demoProcessedInHis}
                  />
                </div>
              </>
            )}

            {/* Transcript (alleen-lezen, gepseudonimiseerd) — echte pijplijn */}
            {completed && (
              <div className="rounded-lg border border-brand-100 bg-white p-4 sm:p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[13px] font-semibold text-ink-900">Transcript</h2>
                  <span className="text-[11px] text-ink-900/50">Gepseudonimiseerd</span>
                </div>
                <textarea
                  value={transcript}
                  readOnly
                  rows={5}
                  className="max-h-[45vh] w-full resize-y rounded-md border border-brand-100 bg-[#f7fafa] px-3 py-2.5 text-[13px] leading-relaxed text-ink-900/80 [field-sizing:content] focus:outline-none"
                />
              </div>
            )}

            {/* Samenvatting voor de Mila-overdracht — echte pijplijn. Alleen
                tonen als de pijplijn er een leverde (oudere taken hebben er
                geen). */}
            {completed && summary !== '' && (
              <SummaryCard value={summary} onChange={setSummary} readOnly={phase === 'saved'} />
            )}

            {/* Gespreksanalyse (ADR 0015) — alleen objectieve maten uit de
                opname (spreektijd, stiltes, tempo); bewust geen emotie- of
                sentimentlabels. Interpretatie is aan de POH. */}
            {completed && metrics && (
              <div className="rounded-lg border border-brand-100 bg-white p-4 sm:p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[13px] font-semibold text-ink-900">Gespreksanalyse</h2>
                  <span className="text-[11px] text-ink-900/50">Objectieve maten</span>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3">
                  {[
                    [
                      'Spreektijd',
                      `${fmt(Math.round(metrics.speechSec))} van ${fmt(Math.round(metrics.audioSec))}`,
                    ],
                    ['Stilte', `${Math.round(metrics.silencePct)}% van de opname`],
                    [
                      'Stiltes van 2 s of langer',
                      metrics.longPauseCount > 0
                        ? `${metrics.pauseCount}, waarvan ${metrics.longPauseCount} ≥ 10 s`
                        : String(metrics.pauseCount),
                    ],
                    ['Langste stilte', `${Math.round(metrics.longestPauseSec)} s`],
                    ['Spreektempo', `${Math.round(metrics.wordsPerMinute)} woorden/min`],
                    ...(metrics.speakers && metrics.speakers.length > 0
                      ? [
                          [
                            'Spreekverhouding',
                            metrics.speakers
                              .map((sp) => `${sp.label} ${Math.round(sp.talkPct)}%`)
                              .join(' · '),
                          ],
                          ['Beurtwisselingen', String(metrics.turnCount ?? 0)],
                        ]
                      : []),
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-[11px] text-ink-900/50">{label}</dt>
                      <dd className="text-[13px] font-medium text-ink-900">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-[11px] leading-relaxed text-ink-900/50">
                  Feitelijke maten uit de opname, zonder duiding. De interpretatie is aan jou.
                </p>
              </div>
            )}

            {/* SOEP-velden — echte pijplijn */}
            {completed && (
              <div className="rounded-lg border border-brand-100 bg-white">
                <div className="border-b border-brand-50 px-4 py-4 sm:px-5">
                  <h2 className="text-[13px] font-semibold text-ink-900">SOEP-verslag</h2>
                  <p className="mt-0.5 text-[11px] text-ink-900/50">
                    {phase === 'saved'
                      ? 'Opgeslagen. Kopieer per veld naar het HIS.'
                      : 'Gepseudonimiseerd. Controleer en bewerk waar nodig, en bevestig.'}
                  </p>
                </div>

                <div className="divide-y divide-brand-50">
                  {(Object.keys(soep) as (keyof Soep)[]).map((key) => (
                    <div
                      key={key}
                      className={`px-4 py-4 sm:px-5 ${key === 'P' ? 'bg-brand-50/50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-900/50">
                          {SOEP_LABELS[key]}
                        </label>
                        <CopyFieldButton text={soep[key]} />
                      </div>
                      <textarea
                        value={soep[key]}
                        onChange={(e) => setSoep((prev) => ({ ...prev, [key]: e.target.value }))}
                        readOnly={phase === 'saved'}
                        rows={3}
                        className="mt-2 max-h-[45vh] w-full resize-y rounded-md border border-brand-100 bg-white px-3 py-2 text-[13px] leading-relaxed text-ink-900 [field-sizing:content] read-only:bg-[#f7fafa] read-only:text-ink-900/80 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                      {key === 'P' && (
                        <MilaHandoff
                          onOpen={openMila}
                          onVerwijsbrief={onVerwijsbrief}
                          defaultOnderwerp={eersteRegel(soep.E)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {phase === 'review' && taskIdRef.current !== null && (
                  <RegenerateRow
                    soepStyle={soepStyle}
                    onStyleChange={changeSoepStyle}
                    onRegenerate={() => void regenerateSoep()}
                  />
                )}

                {errorMsg && (
                  <div className="border-t border-brand-50 px-4 py-4 sm:px-5">
                    <p className="w-full rounded-md border border-error/30 bg-red-50/60 px-3 py-2 text-[12px] text-error">
                      {errorMsg}
                    </p>
                  </div>
                )}

                {/* Afronden: SOEP is overgenomen in het HIS → opname direct
                    definitief verwijderen. Alleen bij een echte servertaak. */}
                {taskIdRef.current !== null && (
                  <HisRow
                    confirming={hisConfirm}
                    busy={hisBusy}
                    onStart={() => setHisConfirm(true)}
                    onCancel={() => setHisConfirm(false)}
                    onConfirm={() => void markProcessedInHis()}
                  />
                )}
              </div>
            )}

            {completed && kennis && <KennisCard text={kennis} />}
          </div>
        </div>
      </div>
    </Layout>
  );
}
